import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Box, Paper, Typography, Grid, Skeleton, Chip, Divider, Card, CardContent, ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, TextField, FormControl, Select, MenuItem, FormControlLabel, Switch, CircularProgress, Stack, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import enhancedFinancialDataService from '../services/EnhancedFinancialDataService';
import TradingView from './TradingView';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, BarChart, Bar, Cell, ReferenceLine, PieChart, Pie } from 'recharts';
import { clearCacheItem, generateCacheKey } from '../utils/cacheManager';
import { formatTimeInAppTimeZone } from '../utils/dateTime';
import {
  normalizeRevenueOperatingSeries,
  getRevenueValue,
  getOperatingIncomeValue,
  getNetIncomeValue,
  isIpoChartPeriod,
  getIpoPeriodLabel,
  isPreliminary8kChartPeriod,
  getPreliminary8kLabel,
  retainOnlyLatestPreliminary8k,
} from '../utils/revenueOperatingData';

const DASHBOARD_EXPLAINER_STYLE_STORAGE_KEY = 'dashboard.explainer.defaultStyle.v1';
const DASHBOARD_EXPLAINER_OVERRIDES_STORAGE_KEY = 'dashboard.explainer.metricStyleOverrides.v1';
const EXPLAINER_STYLE_CONVERSATIONAL = 'conversational';
const EXPLAINER_STYLE_ADVANCED = 'advanced';

const metricHelpText = {
  revenue: {
    conversational: 'Revenue is the company\'s total sales before subtracting expenses.',
    advanced: 'Revenue represents top-line sales recognized in the period under applicable accounting standards.',
  },
  operatingIncome: {
    conversational: 'Operating income is what remains after regular business costs, before interest and taxes.',
    advanced: 'Operating income measures profit from core operations before non-operating items, interest, and taxes.',
  },
  profitMargin: {
    conversational: 'Profit margin tells you how much profit is kept from every dollar of revenue.',
    advanced: 'Profit margin is net income divided by revenue, expressed as a percentage.',
  },
  marketCap: {
    conversational: 'Market cap is the company\'s total stock value: share price multiplied by shares outstanding.',
    advanced: 'Market capitalization equals equity value implied by current market price times diluted shares outstanding.',
  },
  peRatio: {
    conversational: 'P/E compares valuation to annual earnings; higher values usually mean higher growth expectations.',
    advanced: 'Price-to-earnings compares equity value to trailing annualized earnings (proxy here uses operating income).',
  },
  forwardPe: {
    conversational: 'Forward P/E compares today\'s value to next-12-month expected earnings.',
    advanced: 'Forward P/E measures current equity valuation relative to next-twelve-month earnings expectations.',
  },
};

const ETF_EXCHANGE_HINTS = new Set(['NYSEARCA', 'ARCA', 'BATS', 'CBOE', 'NASDAQGM']);
const ETF_BRAND_HINTS = [
  'ISHARES',
  'SPDR',
  'VANGUARD',
  'INVESCO',
  'PROSHARES',
  'SCHWAB',
  'DIREXION',
  'WISDOMTREE',
  'VANECK',
  'GLOBAL X',
  'FIRST TRUST',
  'ARK',
  'BITWISE',
  'GRANITESHARES',
  'ROUNDHILL',
  'DEFIANCE',
  'HASHDEX',
  'FRANKLIN',
  'CALAMOS',
  'LEVERAGESHARES',
  'TIDAL',
  'REX',
  'VOLATILITY SHARES',
  'UNITED STATES',
];
const ETF_STRUCTURE_HINTS = [
  'ETF',
  'ETN',
  'FUND',
  'TRUST',
  'SHARES',
  'INDEX',
  'COMMODITY',
  'BITCOIN',
  'SOLANA',
  'ETHEREUM',
  'FUTURES',
  'TREASURY',
  'BOND',
  'OIL',
  'GOLD',
  'SILVER',
  'INCOME',
];

const hasWholeWordOrPhrase = (text, value) => new RegExp(`\\b${String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`, 'i').test(String(text || ''));

const isLikelyEtfSecurity = ({ tickerSymbol, companyOverview, companyName }) => {
  const tickerText = String(tickerSymbol || '').trim().toUpperCase();
  const nameText = String(companyName?.company_name || companyOverview?.company_name || '').trim().toUpperCase();
  const sectorText = String(companyOverview?.sector || '').trim().toUpperCase();
  const industryText = String(companyOverview?.industry || '').trim().toUpperCase();
  const exchangeText = String(companyOverview?.exchange || '').trim().toUpperCase();

  const hasWord = (text, word) => new RegExp(`\b${String(word || '').replace(/[.*+?^${}()|[\]\\]/g, '\$&')}\b`, 'i').test(text);
  const hasEtfWord = (
    hasWord(nameText, 'ETF')
    || hasWord(nameText, 'ETN')
    || hasWord(industryText, 'EXCHANGE TRADED')
    || hasWord(sectorText, 'EXCHANGE TRADED')
  );
  const hasEtfBrand = ETF_BRAND_HINTS.some((hint) => hasWholeWordOrPhrase(nameText, hint));
  const hasEtfStructure = ETF_STRUCTURE_HINTS.some((hint) => hasWholeWordOrPhrase(nameText, hint));
  const exchangeSuggestsEtf = ETF_EXCHANGE_HINTS.has(exchangeText);
  const hasCommodityStyleName = ['COMMODITY', 'BITCOIN', 'SOLANA', 'ETHEREUM', 'FUTURES', 'TREASURY', 'BOND', 'OIL', 'GOLD', 'SILVER'].some((hint) => nameText.includes(hint));

  // Require at least one strong signal; include exchange + brand/name combo to reduce false positives.
  return Boolean(
    hasEtfWord
    || (hasEtfBrand && hasEtfStructure)
    || (exchangeSuggestsEtf && (hasEtfWord || (hasEtfBrand && hasEtfStructure) || nameText.includes('INDEX') || hasCommodityStyleName))
    || (tickerText.endsWith('U') && hasEtfWord)
  );
};

const CompanyDashboard = ({ ticker, exchange, apiBaseUrl, watchlistIndex = null }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPrice, setCurrentPrice] = useState(null);
  const [priceChange, setPriceChange] = useState(null);
  const [priceLoading, setPriceLoading] = useState(true);
  const [priceLastUpdated, setPriceLastUpdated] = useState(null);
  const [quoteStatusMessage, setQuoteStatusMessage] = useState('');
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [quoteMarketCap, setQuoteMarketCap] = useState(null);
  const [forwardPE, setForwardPE] = useState(null);
  const [forwardOperatingIncomeInput, setForwardOperatingIncomeInput] = useState('');
  const [nextEarningsByTicker, setNextEarningsByTicker] = useState({});
  const [earningsLoading, setEarningsLoading] = useState(false);
  const [currency, setCurrency] = useState('USD');
  const [exchangeRate, setExchangeRate] = useState(null); // Rate to convert currency to USD
  const [showUSD, setShowUSD] = useState(true); // Default to USD display for financial cards/charts
  const [financialPeriodType, setFinancialPeriodType] = useState('quarterly');
  const [effectiveFinancialPeriodType, setEffectiveFinancialPeriodType] = useState('quarterly');
  const [cadenceNotice, setCadenceNotice] = useState('');
  const [chartZoomYears, setChartZoomYears] = useState(10);
  const [indexMarketCap, setIndexMarketCap] = useState(null);
  const [institutionalOwnership, setInstitutionalOwnership] = useState(null);
  const [institutionalOwnershipLoading, setInstitutionalOwnershipLoading] = useState(false);
  const [etfHoldings, setEtfHoldings] = useState([]);
  const [etfHoldingsSource, setEtfHoldingsSource] = useState('');
  const [etfHoldingsLoading, setEtfHoldingsLoading] = useState(false);
  const [etfBalanceSnapshot, setEtfBalanceSnapshot] = useState(null);
  const [etfFundFilings, setEtfFundFilings] = useState([]);
  const [etfFundFilingsLoading, setEtfFundFilingsLoading] = useState(false);
  const [defaultExplainerStyle, setDefaultExplainerStyle] = useState(() => {
    const saved = String(localStorage.getItem(DASHBOARD_EXPLAINER_STYLE_STORAGE_KEY) || EXPLAINER_STYLE_CONVERSATIONAL);
    return saved === EXPLAINER_STYLE_ADVANCED ? EXPLAINER_STYLE_ADVANCED : EXPLAINER_STYLE_CONVERSATIONAL;
  });
  const [metricExplainerOverrides, setMetricExplainerOverrides] = useState(() => {
    try {
      const raw = localStorage.getItem(DASHBOARD_EXPLAINER_OVERRIDES_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  });
  const [data, setData] = useState({
    financialData: [],
    latestMetrics: null,
    previousMetrics: null,
    financialDataQuality: null,
    companyOverview: null,
    companyName: null
  });

  const isWatchlistIndex = Boolean(
    watchlistIndex && Array.isArray(watchlistIndex.tickers)
  );
  const indexTickers = useMemo(() => {
    if (!isWatchlistIndex) return [];
    return Array.from(
      new Set(
        (watchlistIndex.tickers || [])
          .map((item) => (item || '').trim().toUpperCase())
          .filter(Boolean)
      )
    );
  }, [isWatchlistIndex, watchlistIndex]);
  const tickerBundleCacheRef = useRef(new Map());
  const inFlightTickerBundleRefs = useRef(new Map());

  const fetchTickerBundle = useCallback(async (indexTicker, periodType) => {
    const bundleKey = `${String(indexTicker || '').trim().toUpperCase()}:${String(periodType || 'quarterly').trim().toLowerCase()}`;
    const cachedBundle = tickerBundleCacheRef.current.get(bundleKey);
    if (cachedBundle) {
      return cachedBundle;
    }

    const inFlight = inFlightTickerBundleRefs.current.get(bundleKey);
    if (inFlight) {
      return inFlight;
    }

    const request = (async () => {
      const [financialPromise, overviewPromise, namePromise, stockPromise] = [
        enhancedFinancialDataService.getFinancialData(indexTicker, false, periodType),
        enhancedFinancialDataService.getCompanyOverview(indexTicker, true),
        enhancedFinancialDataService.getCompanyName(indexTicker),
        (async () => {
          try {
            const response = await fetch(`${apiBaseUrl || ''}/api/stock-price/${indexTicker}`);
            return response.ok ? await response.json() : null;
          } catch (stockError) {
            console.error(`Failed to fetch stock payload for ${indexTicker}:`, stockError);
            return null;
          }
        })(),
      ];

      const [financialData, companyOverview, companyName, stockPayload] = await Promise.all([
        financialPromise,
        overviewPromise,
        namePromise,
        stockPromise,
      ]);

      const bundle = {
        ticker: indexTicker,
        currency: enhancedFinancialDataService.getCurrency(indexTicker, periodType) || 'USD',
        financialData: Array.isArray(financialData) ? financialData : [],
        companyOverview,
        companyName,
        stockPayload,
      };

      tickerBundleCacheRef.current.set(bundleKey, bundle);
      inFlightTickerBundleRefs.current.delete(bundleKey);
      return bundle;
    })().catch((error) => {
      inFlightTickerBundleRefs.current.delete(bundleKey);
      throw error;
    });

    inFlightTickerBundleRefs.current.set(bundleKey, request);
    return request;
  }, [apiBaseUrl]);

  const indexLabel = isWatchlistIndex
    ? `${watchlistIndex?.name || 'Watchlist'} Index`
    : null;

  const isEtfDashboard = useMemo(() => {
    if (isWatchlistIndex) return false;
    return isLikelyEtfSecurity({
      tickerSymbol: ticker,
      companyOverview: data.companyOverview,
      companyName: data.companyName,
    });
  }, [isWatchlistIndex, ticker, data.companyOverview, data.companyName]);

  useEffect(() => {
    localStorage.setItem(DASHBOARD_EXPLAINER_STYLE_STORAGE_KEY, defaultExplainerStyle);
  }, [defaultExplainerStyle]);

  useEffect(() => {
    localStorage.setItem(
      DASHBOARD_EXPLAINER_OVERRIDES_STORAGE_KEY,
      JSON.stringify(metricExplainerOverrides || {})
    );
  }, [metricExplainerOverrides]);

  const getResolvedExplainerStyle = (metricKey) => {
    const override = metricExplainerOverrides?.[metricKey];
    if (override === EXPLAINER_STYLE_CONVERSATIONAL || override === EXPLAINER_STYLE_ADVANCED) {
      return override;
    }
    return defaultExplainerStyle;
  };

  const getMetricHelpText = (metricKey) => {
    const style = getResolvedExplainerStyle(metricKey);
    const textByStyle = metricHelpText?.[metricKey] || null;
    if (!textByStyle) return '';
    return textByStyle[style] || textByStyle[EXPLAINER_STYLE_CONVERSATIONAL] || '';
  };

  const toggleMetricExplainerStyle = (metricKey) => {
    const currentStyle = getResolvedExplainerStyle(metricKey);
    const nextStyle = currentStyle === EXPLAINER_STYLE_ADVANCED
      ? EXPLAINER_STYLE_CONVERSATIONAL
      : EXPLAINER_STYLE_ADVANCED;

    setMetricExplainerOverrides((prev) => {
      const next = { ...(prev || {}) };
      if (nextStyle === defaultExplainerStyle) {
        delete next[metricKey];
      } else {
        next[metricKey] = nextStyle;
      }
      return next;
    });
  };

  const renderMetricHelpIcon = (metricKey) => {
    const resolvedStyle = getResolvedExplainerStyle(metricKey);
    const hasOverride = Boolean(metricExplainerOverrides?.[metricKey]);
    return (
      <MuiTooltip
        arrow
        enterTouchDelay={0}
        title={(
          <Box sx={{ maxWidth: 320, py: 0.5 }}>
            <Typography variant="body2" sx={{ mb: 0.8 }}>
              {getMetricHelpText(metricKey)}
            </Typography>
            <FormControlLabel
              sx={{ m: 0 }}
              control={(
                <Switch
                  size="small"
                  checked={resolvedStyle === EXPLAINER_STYLE_ADVANCED}
                  onChange={() => toggleMetricExplainerStyle(metricKey)}
                />
              )}
              label={
                <Typography variant="caption">
                  Use advanced wording {hasOverride ? '(metric override)' : '(using default)'}
                </Typography>
              }
            />
          </Box>
        )}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
      </MuiTooltip>
    );
  };

  const renderMetricLabel = (label, metricKey) => (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>
      {renderMetricHelpIcon(metricKey)}
    </Box>
  );

  const getDataQualityChip = (quality) => {
    const status = String(quality?.latestStatus || 'unknown').toLowerCase();
    if (status === 'verified') return { label: 'Data: Verified', color: 'success' };
    if (status === 'estimated') return { label: 'Data: SEC Filings Fallback', color: 'warning' };
    if (status === 'aggregated') return { label: 'Data: Aggregated', color: 'info' };
    return { label: 'Data: Unknown', color: 'default' };
  };

  const renderDataQualityInfo = () => (
    <MuiTooltip
      arrow
      placement="top"
      title={(
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            Data quality status
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Verified: latest period uses the primary normalized financial statement pipeline.
          </Typography>
          <Typography variant="body2">
            SEC Filings Fallback: latest period is derived from SEC filings fallback extraction when the primary path is unavailable.
          </Typography>
        </Box>
      )}
    >
      <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
    </MuiTooltip>
  );

  // Currency symbols for display
  const currencySymbols = {
    USD: '$',
    JPY: '¥',
    EUR: '€',
    GBP: '£',
    CNY: '¥',
    TWD: 'NT$',
    KRW: '₩',
    INR: '₹',
    BRL: 'R$',
    CAD: 'C$',
    AUD: 'A$',
    CHF: 'Fr',
    RMB: '¥',
  };
  
  const getCurrencySymbol = (curr) => currencySymbols[curr] || curr;

  const formatEarningsDate = (dateValue) => {
    if (!dateValue) return 'TBD';
    const parsed = new Date(`${dateValue}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return dateValue;
    return parsed.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatEarningsTime = (timeValue) => {
    if (!timeValue) return 'Time TBD';
    const normalized = String(timeValue).trim();
    if (!normalized) return 'Time TBD';
    return normalized;
  };

  // ADR ratios for foreign stocks (1 ADR = X ordinary shares)
  // Used to correct market cap when SEC reports ordinary shares but price is per ADR
  const adrRatios = {
    TM: 10,     // Toyota: 1 ADR = 10 shares
    HMC: 1,     // Honda: 1 ADR = 1 share
    BABA: 8,    // Alibaba: 1 ADR = 8 shares
    TSM: 5,     // TSMC: 1 ADR = 5 shares
    JD: 2,      // JD.com: 1 ADR = 2 shares
    NIO: 1,     // NIO: 1 ADR = 1 share
    XPEV: 2,    // XPeng: 1 ADR = 2 shares
    BIDU: 8,    // Baidu: 1 ADR = 8 shares
    PDD: 4,     // Pinduoduo: 1 ADR = 4 shares
    LI: 2,      // Li Auto: 1 ADR = 2 shares
    BEKE: 3,    // KE Holdings: 1 ADR = 3 shares
    NTES: 25,   // NetEase: 1 ADR = 25 shares
    ZTO: 1,     // ZTO Express: 1 ADR = 1 share
    ASML: 1,    // ASML: 1:1 (not ADR, direct listing)
    RACE: 1,    // Ferrari: 1:1
    STLA: 1,    // Stellantis: 1:1
  };

  // Format value in USD (for market cap - always in USD since stock trades in USD)
  const formatUSD = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue) || (!numValue && numValue !== 0)) return '$0';
    
    const absValue = Math.abs(numValue);
    if (absValue >= 1e12) {
      return `$${(numValue / 1e12).toFixed(2)}T`;
    } else if (absValue >= 1e9) {
      return `$${(numValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `$${(numValue / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `$${(numValue / 1e3).toFixed(2)}K`;
    }
    return `$${numValue.toFixed(2)}`;
  };

  // Format value with optional USD conversion (for toggling between currencies)
  const formatWithConversion = (value, convertToUSD = false) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(numValue) || (!numValue && numValue !== 0)) return convertToUSD ? '$0' : `${getCurrencySymbol(currency)}0`;
    
    // If converting to USD and we have an exchange rate
    if (convertToUSD && exchangeRate && currency !== 'USD') {
      const usdValue = numValue * exchangeRate;
      return formatUSD(usdValue);
    }
    
    // Use original currency
    const symbol = getCurrencySymbol(currency);
    const absValue = Math.abs(numValue);
    if (absValue >= 1e12) {
      return `${symbol}${(numValue / 1e12).toFixed(2)}T`;
    } else if (absValue >= 1e9) {
      return `${symbol}${(numValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `${symbol}${(numValue / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `${symbol}${(numValue / 1e3).toFixed(2)}K`;
    }
    return `${symbol}${numValue.toFixed(2)}`;
  };

  const getQuoteSymbolCandidates = (baseTicker, exchangeCode) => {
    const base = String(baseTicker || '').trim().toUpperCase();
    const exchangeName = String(exchangeCode || '').trim().toUpperCase();
    if (!base) return [];

    const suffixByExchange = {
      LSE: '.L',
      TSX: '.TO',
      TSXV: '.V',
      ASX: '.AX',
      XETRA: '.DE',
      FWB: '.DE',
      SIX: '.SW',
      SWX: '.SW',
      EPA: '.PA',
      BIT: '.MI',
      JPX: '.T',
      TSE: '.T',
      HKEX: '.HK',
      NSE: '.NS',
      BSE: '.BO',
      B3: '.SA',
      SAO: '.SA',
      KRX: '.KS',
      TPE: '.TW',
      TWSE: '.TW',
      SZSE: '.SZ',
      SSE: '.SS',
      JSE: '.JO',
      BME: '.MC',
    };

    const candidates = new Set([base]);
    const preferred = suffixByExchange[exchangeName];
    if (preferred && !base.endsWith(preferred)) {
      candidates.add(`${base}${preferred}`);
    }

    ['.L', '.TO', '.AX', '.HK', '.T', '.NS', '.BO', '.DE', '.PA', '.SW', '.MI'].forEach((suffix) => {
      if (!base.endsWith(suffix)) {
        candidates.add(`${base}${suffix}`);
      }
    });

    return Array.from(candidates);
  };

  useEffect(() => {
    setForwardOperatingIncomeInput('');
    if (!isWatchlistIndex && !ticker) return;
    if (isWatchlistIndex && indexTickers.length === 0) {
      setError('Selected watchlist has no tickers. Add at least one ticker to build the index.');
      setLoading(false);
      return;
    }
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Configure API base URL if provided
        if (apiBaseUrl) {
          enhancedFinancialDataService.apiBaseUrl = apiBaseUrl;
        }
        
        if (isWatchlistIndex) {
          const rateCache = { USD: 1 };
          const getRateToUsd = async (fromCurrency) => {
            const curr = (fromCurrency || 'USD').toUpperCase();
            if (rateCache[curr]) return rateCache[curr];
            try {
              const response = await fetch(`${apiBaseUrl || ''}/api/exchange-rate/${curr}/USD`);
              const payload = await response.json();
              const parsed = Number(payload?.rate);
              if (Number.isFinite(parsed) && parsed > 0) {
                rateCache[curr] = parsed;
                return parsed;
              }
            } catch (rateError) {
              console.error(`Failed to load FX ${curr}/USD for watchlist index:`, rateError);
            }
            return null;
          };

          const perTickerData = await Promise.all(
            indexTickers.map((indexTicker) => fetchTickerBundle(indexTicker, financialPeriodType))
          );

          const byPeriod = new Map();
          const sectorCounts = {};
          let totalMarketCap = 0;

          const getTickerMarketCap = (tickerBundle) => {
            const quoteCap = Number(tickerBundle.stockPayload?.marketCap);
            if (Number.isFinite(quoteCap) && quoteCap > 0) {
              return quoteCap;
            }

            const currentPrice = Number(tickerBundle.stockPayload?.price);
            const outstandingShares = Number(tickerBundle.companyOverview?.outstanding_shares);
            const ratio = adrRatios[tickerBundle.ticker] || 1;

            if (
              Number.isFinite(currentPrice) && currentPrice > 0
              && Number.isFinite(outstandingShares) && outstandingShares > 0
            ) {
              return (outstandingShares / ratio) * currentPrice;
            }

            return 0;
          };

          for (const tickerBundle of perTickerData) {
            const rate = await getRateToUsd(tickerBundle.currency);
            if (!Number.isFinite(rate) || rate <= 0) {
              continue;
            }

            totalMarketCap += getTickerMarketCap(tickerBundle);

            const sector = tickerBundle.companyOverview?.sector;
            if (sector) {
              sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
            }

            for (const row of tickerBundle.financialData) {
              const periodKey = new Date(row.period).toISOString().split('T')[0];
              const revenue = getRevenueValue(row);
              const operatingIncome = getOperatingIncomeValue(row);
              const revenueUsd = Number.isFinite(revenue) ? revenue * rate : 0;
              const operatingIncomeUsd = Number.isFinite(operatingIncome) ? operatingIncome * rate : 0;

              const existing = byPeriod.get(periodKey) || {
                period: row.period,
                Revenue: 0,
                'Operating Income': 0,
                constituents: 0,
              };
              existing.Revenue += revenueUsd;
              existing['Operating Income'] += operatingIncomeUsd;
              existing.constituents += 1;
              byPeriod.set(periodKey, existing);
            }
          }

          const sortedData = [...byPeriod.values()].sort((a, b) =>
            new Date(a.period) - new Date(b.period)
          );

          if (!sortedData.length) {
            setError('No financial data could be aggregated for this watchlist index.');
            setLoading(false);
            return;
          }

          const latest = sortedData[sortedData.length - 1];
          const previous = sortedData[sortedData.length - 2] || null;
          const latestRevenue = Number(latest?.Revenue) || 0;
          const latestOperatingIncome = Number(latest?.['Operating Income']) || 0;
          const previousRevenue = Number(previous?.Revenue) || 0;
          const previousOperatingIncome = Number(previous?.['Operating Income']) || 0;

          setCurrency('USD');
          setExchangeRate(1);
          setShowUSD(true);
          setCurrentPrice(null);
          setPriceChange(null);
          setQuoteMarketCap(null);
          setForwardPE(null);
          setPriceLoading(false);
          setIndexMarketCap(totalMarketCap > 0 ? totalMarketCap : null);

          setData({
            financialData: sortedData,
            latestMetrics: {
              revenue: latestRevenue,
              operating_income: latestOperatingIncome,
              profit_margin: latestRevenue > 0 ? (latestOperatingIncome / latestRevenue) * 100 : 0,
            },
            previousMetrics: previous
              ? {
                  revenue: previousRevenue,
                  operating_income: previousOperatingIncome,
                  profit_margin: previousRevenue > 0 ? (previousOperatingIncome / previousRevenue) * 100 : 0,
                }
              : null,
            financialDataQuality: {
              latestStatus: 'aggregated',
              latestConfidence: null,
              hasSecFallbackRows: false,
              droppedScaleOutliers: 0,
              warnings: [],
            },
            companyOverview: {
              company_name: indexLabel,
              exchange: 'INDEX',
              sector: 'Watchlist Composite',
              industry: Object.keys(sectorCounts)
                .sort((a, b) => (sectorCounts[b] || 0) - (sectorCounts[a] || 0))[0] || 'Mixed',
              currency: 'USD',
              country: 'Multi',
              address: indexTickers.join(', '),
            },
            companyName: {
              company_name: indexLabel,
            },
          });
          setEffectiveFinancialPeriodType(financialPeriodType);
          setCadenceNotice('');
        } else {
          setIndexMarketCap(null);
          // Clear cache to ensure fresh data
          const financialCacheKeyQuarterly = generateCacheKey('financial_data_v36_quarterly', ticker);
          const financialCacheKeyAnnual = generateCacheKey('financial_data_v36_annual', ticker);
          const companyOverviewCacheKey = generateCacheKey('company_overview', ticker);
          const companyNameCacheKey = generateCacheKey('company_name', ticker);
          clearCacheItem(financialCacheKeyQuarterly);
          clearCacheItem(financialCacheKeyAnnual);
          clearCacheItem(companyOverviewCacheKey);
          clearCacheItem(companyNameCacheKey);

          // Fetch profile metadata first so we can branch dashboard behavior for ETFs.
          const companyOverview = await enhancedFinancialDataService.getCompanyOverview(ticker, true);
          const companyName = await enhancedFinancialDataService.getCompanyName(ticker);
          const likelyEtfEntity = isLikelyEtfSecurity({
            tickerSymbol: ticker,
            companyOverview,
            companyName,
          });

          // Get financial data
          let financialData = await enhancedFinancialDataService.getFinancialData(ticker, false, financialPeriodType);
          if (!Array.isArray(financialData) || financialData.length === 0) {
            financialData = await enhancedFinancialDataService.getFinancialData(ticker, true, financialPeriodType);
          }
          let resolvedPeriodType = financialPeriodType;
          let financialDataQuality = enhancedFinancialDataService.getFinancialDataMeta(ticker, financialPeriodType);

          if (!Array.isArray(financialData) || financialData.length === 0) {
            const fallbackPeriodType = financialPeriodType === 'annual' ? 'quarterly' : 'annual';
            const fallbackSeries = await enhancedFinancialDataService.getFinancialData(ticker, false, fallbackPeriodType);
            if (Array.isArray(fallbackSeries) && fallbackSeries.length > 0) {
              financialData = fallbackSeries;
              resolvedPeriodType = fallbackPeriodType;
              financialDataQuality = enhancedFinancialDataService.getFinancialDataMeta(ticker, fallbackPeriodType);
              setCadenceNotice(
                `No ${financialPeriodType} data found for ${ticker}. Showing ${fallbackPeriodType} data instead.`
              );
            } else {
              setCadenceNotice(`No ${financialPeriodType} data found for ${ticker}.`);
            }
          } else {
            setCadenceNotice('');
          }
          setEffectiveFinancialPeriodType(resolvedPeriodType);

          // Get currency for this ticker
          const tickerCurrency = enhancedFinancialDataService.getCurrency(ticker, resolvedPeriodType);
          setCurrency(tickerCurrency);
          setShowUSD(true);

          if (!financialData || financialData.length === 0) {
            if (!likelyEtfEntity) {
              setError(`No financial data available for ${ticker}. This ticker may be an ETF, SPAC, OTC stock, or index that doesn't have financial statements in our database. Please select a stock with available financial data.`);
              setLoading(false);
              return;
            }

            // ETFs can still render a useful dashboard from balance-sheet/fund-holdings sources.
            financialData = [];
            financialDataQuality = {
              latestStatus: 'unknown',
              latestConfidence: null,
              hasSecFallbackRows: false,
              droppedScaleOutliers: 0,
              warnings: ['ETF selected: financial statement trend rows are limited, so ETF-specific metrics are shown.'],
            };
            setCadenceNotice('ETF selected: showing ETF-specific metrics and holdings snapshot.');
          }

          // Sort data by date
          const sortedData = [...financialData].sort((a, b) =>
            new Date(a.period) - new Date(b.period)
          );

          const normalizedRows = resolvedPeriodType === 'annual'
            ? sortedData
            : normalizeRevenueOperatingSeries(sortedData, 10);

          // Keep card metrics on the same filtered timeline used by the chart.
          const preferredMetricRows = resolvedPeriodType === 'annual'
            ? normalizedRows
            : normalizedRows.filter((row, index) => {
              if (index === 0) return true;
              return !isLikelyCumulativeAnnualRow(row, normalizedRows[index - 1]);
            });
          const metricRows = resolvedPeriodType === 'annual'
            ? normalizedRows
            : (preferredMetricRows.length >= 2 ? preferredMetricRows : normalizedRows);

          const latestRow = metricRows.length > 0 ? metricRows[metricRows.length - 1] : null;
          const previousRow = metricRows.length > 1 ? metricRows[metricRows.length - 2] : null;

          const latestRevenue = latestRow ? getRevenueValue(latestRow) : null;
          const latestOperatingIncome = latestRow ? getOperatingIncomeValue(latestRow) : null;
          const previousRevenue = previousRow ? getRevenueValue(previousRow) : null;
          const previousOperatingIncome = previousRow ? getOperatingIncomeValue(previousRow) : null;

          const latestMetrics = latestRow
            ? {
                revenue: Number.isFinite(latestRevenue) ? latestRevenue : 0,
                operating_income: Number.isFinite(latestOperatingIncome) ? latestOperatingIncome : 0,
                profit_margin: Number.isFinite(latestRevenue) && latestRevenue > 0 && Number.isFinite(latestOperatingIncome)
                  ? (latestOperatingIncome / latestRevenue) * 100
                  : 0,
              }
            : null;

          const previousMetrics = previousRow
            ? {
                revenue: Number.isFinite(previousRevenue) ? previousRevenue : 0,
                operating_income: Number.isFinite(previousOperatingIncome) ? previousOperatingIncome : 0,
                profit_margin: Number.isFinite(previousRevenue) && previousRevenue > 0 && Number.isFinite(previousOperatingIncome)
                  ? (previousOperatingIncome / previousRevenue) * 100
                  : 0,
              }
            : null;

          // Fetch current stock price
          fetchCurrentStockPrice(ticker, companyOverview?.exchange || exchange);

          setData({
            financialData: sortedData,
            latestMetrics,
            previousMetrics,
            financialDataQuality,
            companyOverview,
            companyName
          });
        }
      } catch (error) {
        console.error('Error fetching company dashboard data:', error);
        const dashboardEntity = isWatchlistIndex ? indexLabel : ticker;
        setError(`Failed to load data for ${dashboardEntity}. ${error.message || ''}`);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [ticker, apiBaseUrl, isWatchlistIndex, indexTickers, indexLabel, financialPeriodType]);

  // Fetch exchange rate when currency changes (for converting financials to USD)
  useEffect(() => {
    const fetchExchangeRate = async () => {
      if (currency === 'USD') {
        setExchangeRate(1);
        return;
      }
      
      try {
        const response = await fetch(`${apiBaseUrl || ''}/api/exchange-rate/${currency}/USD`);
        const data = await response.json();
        
        if (data && data.rate) {
          setExchangeRate(data.rate);
          console.log(`Exchange rate ${currency} -> USD: ${data.rate}`);
        } else {
          setExchangeRate(null);
        }
      } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
        setExchangeRate(null);
      }
    };
    
    fetchExchangeRate();
  }, [currency, apiBaseUrl]);

  useEffect(() => {
    const analyzedTickers = isWatchlistIndex
      ? indexTickers
      : [String(ticker || '').trim().toUpperCase()].filter(Boolean);

    if (analyzedTickers.length === 0) {
      setNextEarningsByTicker({});
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    const fetchNextEarnings = async () => {
      setEarningsLoading(true);
      try {
        const response = await fetch(
          `${apiBaseUrl || ''}/api/earnings/calendar?days=120&limit=2000`,
          { signal: controller.signal }
        );

        if (!response.ok) {
          throw new Error(`Earnings calendar request failed (HTTP ${response.status})`);
        }

        const payload = await response.json();
        const events = Array.isArray(payload?.events) ? payload.events : [];
        const wanted = new Set(analyzedTickers);

        const nextByTicker = {};
        for (const event of events) {
          const symbol = String(event?.symbol || event?.ticker || '').trim().toUpperCase();
          if (!symbol || !wanted.has(symbol) || nextByTicker[symbol]) {
            continue;
          }
          nextByTicker[symbol] = {
            date: event?.date || null,
            time: event?.time || null,
          };
        }

        for (const symbol of analyzedTickers) {
          if (!nextByTicker[symbol]) {
            nextByTicker[symbol] = null;
          }
        }

        if (!cancelled) {
          setNextEarningsByTicker(nextByTicker);
        }
      } catch (fetchError) {
        if (fetchError?.name === 'AbortError') return;
        console.error('Failed to load next earnings calendar data:', fetchError);
        if (!cancelled) {
          setNextEarningsByTicker({});
        }
      } finally {
        if (!cancelled) {
          setEarningsLoading(false);
        }
      }
    };

    fetchNextEarnings();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [isWatchlistIndex, indexTickers, ticker, apiBaseUrl]);

  useEffect(() => {
    if (isWatchlistIndex || isEtfDashboard || !ticker) {
      setInstitutionalOwnership(null);
      setInstitutionalOwnershipLoading(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();

    const fetchInstitutionalOwnership = async () => {
      setInstitutionalOwnershipLoading(true);
      try {
        const baseUrl = `${apiBaseUrl || ''}/api/institutional-ownership/${encodeURIComponent(String(ticker).trim().toUpperCase())}`;
        const response = await fetch(baseUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Institutional ownership request failed (HTTP ${response.status})`);
        }
        let payload = await response.json();

        if (!payload?.available) {
          const retryResponse = await fetch(`${baseUrl}?refresh=1`, { signal: controller.signal });
          if (retryResponse.ok) {
            payload = await retryResponse.json();
          }
        }

        if (!cancelled) {
          setInstitutionalOwnership(payload && typeof payload === 'object' ? payload : null);
        }
      } catch (fetchError) {
        if (fetchError?.name === 'AbortError') return;
        console.error('Failed to load institutional ownership:', fetchError);
        if (!cancelled) {
          setInstitutionalOwnership(null);
        }
      } finally {
        if (!cancelled) {
          setInstitutionalOwnershipLoading(false);
        }
      }
    };

    fetchInstitutionalOwnership();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [apiBaseUrl, isEtfDashboard, isWatchlistIndex, ticker]);

  useEffect(() => {
    if (isWatchlistIndex || !isEtfDashboard || !ticker) {
      setEtfHoldings([]);
      setEtfHoldingsSource('');
      setEtfBalanceSnapshot(null);
      setEtfHoldingsLoading(false);
      setEtfFundFilings([]);
      setEtfFundFilingsLoading(false);
      return;
    }

    let cancelled = false;

    const toFiniteOrNull = (value) => {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const fetchEtfSupplementalData = async () => {
      setEtfHoldingsLoading(true);
      setEtfFundFilingsLoading(true);
      try {
        const symbol = String(ticker || '').trim().toUpperCase();

        const [holdingsResponse, balanceRows, fundFilingsResponse] = await Promise.all([
          fetch(`${apiBaseUrl || ''}/api/etf-holdings/${encodeURIComponent(symbol)}`),
          enhancedFinancialDataService.getBalanceSheet(symbol),
          fetch(`${apiBaseUrl || ''}/api/sec-filings/${encodeURIComponent(symbol)}/fund?limit=30`),
        ]);

        let holdingsPayload = null;
        if (holdingsResponse.ok) {
          holdingsPayload = await holdingsResponse.json();
        }

        let fundFilingsPayload = null;
        if (fundFilingsResponse.ok) {
          fundFilingsPayload = await fundFilingsResponse.json();
        }

        const normalizedHoldings = Array.isArray(holdingsPayload?.holdings)
          ? holdingsPayload.holdings
              .map((item) => {
                const symbolValue = String(item?.symbol || '').trim().toUpperCase();
                const name = String(item?.name || symbolValue).trim();
                const weight = Number(item?.weight);
                return {
                  symbol: symbolValue,
                  name,
                  weight: Number.isFinite(weight) ? weight : null,
                };
              })
              .filter((item) => item.symbol)
          : [];

        const latestBalanceRow = (Array.isArray(balanceRows) ? [...balanceRows] : [])
          .filter((row) => row && row.period)
          .sort((a, b) => new Date(b.period).getTime() - new Date(a.period).getTime())[0] || null;

        const assets = toFiniteOrNull(latestBalanceRow?.assets);
        const liabilities = toFiniteOrNull(latestBalanceRow?.liabilities);
        const cash = toFiniteOrNull(latestBalanceRow?.cash);
        const netAssets = Number.isFinite(assets) && Number.isFinite(liabilities)
          ? assets - liabilities
          : (Number.isFinite(assets) ? assets : null);
        const fundSnapshot = fundFilingsPayload?.snapshot && typeof fundFilingsPayload.snapshot === 'object'
          ? fundFilingsPayload.snapshot
          : null;

        if (!cancelled) {
          setEtfHoldings(normalizedHoldings);
          setEtfHoldingsSource(String(holdingsPayload?.source || '').trim());
          setEtfBalanceSnapshot(fundSnapshot || (latestBalanceRow
            ? {
                period: latestBalanceRow.period,
                assets,
                liabilities,
                cash,
                netAssets,
              }
            : null));
          setEtfFundFilings(Array.isArray(fundFilingsPayload?.filings) ? fundFilingsPayload.filings : []);
        }
      } catch (error) {
        console.error('Failed to load ETF supplemental dashboard data:', error);
        if (!cancelled) {
          setEtfHoldings([]);
          setEtfHoldingsSource('');
          setEtfBalanceSnapshot(null);
          setEtfFundFilings([]);
        }
      } finally {
        if (!cancelled) {
          setEtfHoldingsLoading(false);
          setEtfFundFilingsLoading(false);
        }
      }
    };

    fetchEtfSupplementalData();

    return () => {
      cancelled = true;
    };
  }, [apiBaseUrl, isEtfDashboard, isWatchlistIndex, ticker]);

  // Fetch the current stock price
  const fetchCurrentStockPrice = async (ticker, exchangeCode = '') => {
    setPriceLoading(true);
    setForwardPE(null);
    setQuoteStatusMessage('');
    setQuoteDetails(null);
    try {
      const candidates = getQuoteSymbolCandidates(ticker, exchangeCode);
      let resolvedQuote = null;
      let resolvedSymbol = String(ticker || '').trim().toUpperCase();

      for (const candidate of candidates) {
        const response = await fetch(`${apiBaseUrl}/api/stock-price/${encodeURIComponent(candidate)}`);
        const data = await response.json();
        if (data && Number.isFinite(Number(data.price)) && Number(data.price) > 0) {
          resolvedQuote = data;
          resolvedSymbol = candidate;
          break;
        }
      }

      if (resolvedQuote) {
        setCurrentPrice(Number(resolvedQuote.price));
        setQuoteDetails(resolvedQuote);
        if (resolvedQuote.change !== undefined) {
          setPriceChange(resolvedQuote.change);
        } else {
          setPriceChange(null);
        }
        const parsedMarketCap = Number(resolvedQuote.marketCap);
        setQuoteMarketCap(Number.isFinite(parsedMarketCap) && parsedMarketCap > 0 ? parsedMarketCap : null);
        const parsedForwardPe = Number(resolvedQuote.forwardPE);
        if (Number.isFinite(parsedForwardPe) && parsedForwardPe > 0) {
          setForwardPE(parsedForwardPe);
        }
        setPriceLastUpdated(new Date());
        if (resolvedQuote.source === 'Ghana Stock Exchange') {
          setQuoteStatusMessage(`Ghana Stock Exchange close${resolvedQuote.asOf ? ` as of ${resolvedQuote.asOf}` : ''}`);
        } else if (resolvedSymbol !== String(ticker || '').trim().toUpperCase()) {
          setQuoteStatusMessage(`Using fallback quote symbol: ${resolvedSymbol}`);
        }
      } else {
        // Fallback to YahooFinance API directly
        const yahooResponse = await fetch(`https://query1.finance.yahoo.com/v8/finance/chart/${ticker}?interval=1d`);
        const yahooData = await yahooResponse.json();
        
        if (yahooData && 
            yahooData.chart && 
            yahooData.chart.result && 
            yahooData.chart.result[0] && 
            yahooData.chart.result[0].meta) {
          
          const meta = yahooData.chart.result[0].meta;
          setCurrentPrice(meta.regularMarketPrice);
          setQuoteMarketCap(null);
          setPriceChange(null);
          setQuoteStatusMessage('Using direct Yahoo fallback quote feed.');
          
          // Calculate price change
          const previousClose = meta.previousClose;
          if (previousClose) {
            const change = meta.regularMarketPrice - previousClose;
            setPriceChange({
              value: change,
              percent: (change / previousClose) * 100
            });
          }
          
          setPriceLastUpdated(new Date());
          setForwardPE(null);
        } else {
          // If Yahoo API fails, try another approach using a simple proxy
          try {
            const proxyResponse = await fetch(`https://cors-anywhere.herokuapp.com/https://finance.yahoo.com/quote/${ticker}`);
            const html = await proxyResponse.text();
            
            // Simple regex to extract the price
            const priceMatch = html.match(/"regularMarketPrice":{"raw":([\d\.]+)/);
            if (priceMatch && priceMatch[1]) {
              setCurrentPrice(parseFloat(priceMatch[1]));
              setQuoteMarketCap(null);
              setForwardPE(null);
              setPriceChange(null);
              setQuoteStatusMessage('Using HTML quote fallback feed.');
            } else {
              console.warn('Could not extract stock price from Yahoo Finance');
              setCurrentPrice(null);
              setQuoteMarketCap(null);
              setPriceChange(null);
              setQuoteStatusMessage('Live price feed unavailable for this symbol/exchange.');
            }
          } catch (e) {
            console.error('Error with proxy approach:', e);
            // Show null price if all sources fail
            setCurrentPrice(null);
            setQuoteMarketCap(null);
            setPriceChange(null);
            setQuoteStatusMessage('Live price feed unavailable for this symbol/exchange.');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching current stock price:', error);
      // Show null price if fetch fails
      setCurrentPrice(null);
      setQuoteMarketCap(null);
      setPriceChange(null);
      setQuoteStatusMessage('Live price feed unavailable for this symbol/exchange.');
    } finally {
      setPriceLoading(false);
    }
  };

  // Helper function to refresh stock price
  const refreshStockPrice = () => {
    if (isWatchlistIndex) return;
    fetchCurrentStockPrice(ticker, data.companyOverview?.exchange || exchange);
  };

  // Helper functions
  const formatCurrency = (value) => {
    // Convert to number and handle invalid values
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    const symbol = getCurrencySymbol(currency);
    
    if (isNaN(numValue) || (!numValue && numValue !== 0)) return `${symbol}0`;
    
    const absValue = Math.abs(numValue);
    if (absValue >= 1e12) {
      return `${symbol}${(numValue / 1e12).toFixed(2)}T`;
    } else if (absValue >= 1e9) {
      return `${symbol}${(numValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `${symbol}${(numValue / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `${symbol}${(numValue / 1e3).toFixed(2)}K`;
    }
    return `${symbol}${numValue.toFixed(2)}`;
  };

  const formatChartCurrency = (value) => {
    const numValue = typeof value === 'number' ? value : parseFloat(value);
    const shouldConvertToUsd = showUSD && currency !== 'USD' && Number.isFinite(exchangeRate) && exchangeRate > 0;
    const displayValue = shouldConvertToUsd ? numValue * exchangeRate : numValue;
    const symbol = showUSD ? '$' : getCurrencySymbol(currency);

    if (isNaN(displayValue) || (!displayValue && displayValue !== 0)) return `${symbol}0`;

    const absValue = Math.abs(displayValue);
    if (absValue >= 1e12) {
      return `${symbol}${(displayValue / 1e12).toFixed(2)}T`;
    } else if (absValue >= 1e9) {
      return `${symbol}${(displayValue / 1e9).toFixed(2)}B`;
    } else if (absValue >= 1e6) {
      return `${symbol}${(displayValue / 1e6).toFixed(2)}M`;
    } else if (absValue >= 1e3) {
      return `${symbol}${(displayValue / 1e3).toFixed(2)}K`;
    }
    return `${symbol}${displayValue.toFixed(2)}`;
  };

  // Accept values like 1,200,000 or 1.2M / 1B / 750K when users estimate forward income.
  const parseHumanReadableNumber = (input) => {
    if (input === null || input === undefined) return NaN;
    const raw = String(input).trim();
    if (!raw) return NaN;

    const normalized = raw.replace(/,/g, '').replace(/\s+/g, '');
    const match = normalized.match(/^([+-]?(?:\d+\.?\d*|\.\d+))(?:([kmbt]))?$/i);
    if (!match) return NaN;

    const base = Number(match[1]);
    if (!Number.isFinite(base)) return NaN;

    const suffix = (match[2] || '').toUpperCase();
    const multipliers = {
      '': 1,
      K: 1e3,
      M: 1e6,
      B: 1e9,
      T: 1e12,
    };

    return base * (multipliers[suffix] || 1);
  };

  const formatAccountingNumber = (value) => {
    if (!Number.isFinite(value)) return '';
    return value.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const formatCompactNumber = (value) => {
    if (!Number.isFinite(value)) return '';
    const absValue = Math.abs(value);
    if (absValue >= 1e12) return `${(value / 1e12).toFixed(2)}T`;
    if (absValue >= 1e9) return `${(value / 1e9).toFixed(2)}B`;
    if (absValue >= 1e6) return `${(value / 1e6).toFixed(2)}M`;
    if (absValue >= 1e3) return `${(value / 1e3).toFixed(2)}K`;
    return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  // Identify likely cumulative year-end / 10-K totals so they aren't treated as
  // standalone quarterly periods in cards/valuation logic.
  // Never use this filter when the dashboard is already on the Annual series —
  // every point is an intentional FY total (stripping them leaves only 2 dots).
  const isLikelyCumulativeAnnualRow = (currentRow, previousRow) => {
    if (!currentRow || !previousRow) return false;
    if (effectiveFinancialPeriodType === 'annual') return false;

    // Residual Q4 from 10-K (Annual - Q1-Q3) is a real quarterly point — keep it.
    if (
      currentRow?.isDerivedQuarter === true
      || String(currentRow?.sourceOrigin || '').toLowerCase() === 'derived-from-10k'
    ) {
      return false;
    }

    const currentDate = new Date(currentRow.period);
    const previousDate = new Date(previousRow.period);
    if (!Number.isFinite(currentDate.getTime()) || !Number.isFinite(previousDate.getTime())) {
      return false;
    }

    const currentForm = String(currentRow.sourceForm || '').toUpperCase();
    const currentFiscalPeriod = String(currentRow.sourceFiscalPeriod || '').toUpperCase();
    if (
      currentForm === '10-K'
      || currentForm === '10-K/A'
      || currentForm === '20-F'
      || currentForm === '20-F/A'
      || currentForm === '40-F'
      || currentForm === '40-F/A'
      || currentFiscalPeriod === 'FY'
    ) {
      return true;
    }

    const currentRevenue = Number(currentRow.Revenue);
    const previousRevenue = Number(previousRow.Revenue);
    if (!Number.isFinite(currentRevenue) || !Number.isFinite(previousRevenue) || previousRevenue <= 0) {
      return false;
    }

    const daySpan = (currentDate.getTime() - previousDate.getTime()) / (1000 * 60 * 60 * 24);
    // Annual totals are typically ~3-5x a prior quarter. Use a high multiple so
    // genuine high-growth quarters are not dropped from the chart.
    if (currentRevenue > previousRevenue * 2.5 && daySpan >= 60 && daySpan <= 400) {
      return true;
    }

    const currentMonth = currentDate.getUTCMonth() + 1;
    // Calendar/fiscal year-end style months with a large step-up vs prior quarter.
    if ([1, 3, 12].includes(currentMonth) && daySpan >= 120 && daySpan <= 260) {
      return currentRevenue > previousRevenue * 2.2;
    }

    return false;
  };
  
  const getPercentChange = (current, previous) => {
    if (!previous || previous === 0) return { value: 0, isPositive: true };
    const change = ((current - previous) / Math.abs(previous)) * 100;
    return { 
      value: Math.abs(change).toFixed(2), 
      isPositive: change >= 0 
    };
  };

  const normalizedRevenueOperatingRows = useMemo(
    () => normalizeRevenueOperatingSeries(data.financialData || [], 10),
    [data.financialData]
  );

  const sortedReportingPeriods = normalizedRevenueOperatingRows
    .filter((item) => Number.isFinite(new Date(item.period).getTime()))
    .sort((a, b) => new Date(a.period).getTime() - new Date(b.period).getTime());

  // Quarterly charts drop embedded 10-K / FY totals so residual Q4 isn't double-counted.
  // Annual charts are ONLY those FY rows — do not strip them down to first+last.
  const chartReportingPeriods = effectiveFinancialPeriodType === 'annual'
    ? sortedReportingPeriods
    : sortedReportingPeriods.filter((row, index) => {
      if (index === 0) return true;
      if (index === sortedReportingPeriods.length - 1) return true;
      return !isLikelyCumulativeAnnualRow(row, sortedReportingPeriods[index - 1]);
    });

  const latestReportingDate = chartReportingPeriods.length
    ? new Date(chartReportingPeriods[chartReportingPeriods.length - 1].period)
    : null;

  const reportingPeriods = latestReportingDate
    ? chartReportingPeriods.filter((item) => {
        const itemDate = new Date(item.period);
        const yearsDiff =
          latestReportingDate.getFullYear() - itemDate.getFullYear() -
          (latestReportingDate.getMonth() < itemDate.getMonth() ||
          (latestReportingDate.getMonth() === itemDate.getMonth() && latestReportingDate.getDate() < itemDate.getDate())
            ? 1
            : 0);
        return yearsDiff < chartZoomYears;
      })
    : [];

  // Preliminary 8-K standout is only valid on the single latest reporting point.
  const reportingPeriodsLatestPrelimOnly = retainOnlyLatestPreliminary8k(reportingPeriods);

  const chartData = reportingPeriodsLatestPrelimOnly.map((item) => {
    const companyName = data.companyName?.company_name || data.companyOverview?.company_name || ticker;
    const formattedPeriod = new Date(item.period).toLocaleDateString('en-US', {
      year: '2-digit',
      month: 'short'
    });
    const isIpoPeriod = isIpoChartPeriod(item);
    const ipoLabel = isIpoPeriod ? getIpoPeriodLabel(item.sourceForm, item.ipoLabel) : null;
    const isPreliminary = isPreliminary8kChartPeriod(item) && item.isPreliminary === true;
    const preliminaryLabel = isPreliminary
      ? getPreliminary8kLabel(item.preliminaryLabel)
      : null;
    const displayPeriod = isIpoPeriod
      ? `${formattedPeriod} · ${ipoLabel}`
      : (isPreliminary
        ? `${formattedPeriod} · ${preliminaryLabel || getPreliminary8kLabel()}`
        : formattedPeriod);
    const operatingIncomeValue = getOperatingIncomeValue(item);
    const netIncomeValue = getNetIncomeValue(item);
    const hasOperatingIncome = Number.isFinite(operatingIncomeValue);
    const hasNetIncome = Number.isFinite(netIncomeValue);

    let operatingIncomePositive = null;
    let operatingIncomeNegative = null;
    if (hasOperatingIncome) {
      if (operatingIncomeValue >= 0) {
        operatingIncomePositive = operatingIncomeValue;
      } else {
        operatingIncomeNegative = operatingIncomeValue;
      }
    }

    return {
      period: displayPeriod,
      periodRaw: item.period,
      company: companyName,
      xAxisLabel: `${companyName} - ${displayPeriod}`,
      Revenue: getRevenueValue(item),
      "Operating Income": operatingIncomeValue,
      "Net Income": netIncomeValue,
      operatingIncomePositive,
      operatingIncomeNegative,
      operatingIncomeValue: hasOperatingIncome ? operatingIncomeValue : null,
      netIncomeValue: hasNetIncome ? netIncomeValue : null,
      isIpoPeriod,
      ipoLabel,
      isPreliminary,
      preliminaryLabel,
      sourceForm: item.sourceForm || null,
      sourceOrigin: item.sourceOrigin || null,
    };
  });

  const outstandingShares = Number(data.companyOverview?.outstanding_shares);
  const overviewMarketCap = Number(data.companyOverview?.market_cap);
  const hasOutstandingShares = Number.isFinite(outstandingShares) && outstandingShares > 0;
  const hasCurrentPrice = Number.isFinite(currentPrice) && currentPrice > 0;
  
  // Apply ADR ratio correction: SEC reports ordinary shares, but price is per ADR
  // 1 ADR = N ordinary shares, so: ADR count = ordinary shares / ratio
  // Market cap = (ordinary shares / ratio) * ADR price
  const adrRatio = adrRatios[ticker] || 1;
  const computedMarketCap = hasOutstandingShares && hasCurrentPrice
    ? (outstandingShares / adrRatio) * currentPrice
    : null;
  const singleTickerMarketCapNative = Number.isFinite(quoteMarketCap) && quoteMarketCap > 0
    ? quoteMarketCap
    : (Number.isFinite(overviewMarketCap) && overviewMarketCap > 0 ? overviewMarketCap : computedMarketCap);
  const hasExchangeRate = exchangeRate !== null && Number.isFinite(exchangeRate) && exchangeRate > 0;
  const marketCap = isWatchlistIndex
    ? indexMarketCap
    : (currency !== 'USD' && showUSD && hasExchangeRate && Number.isFinite(singleTickerMarketCapNative)
      ? singleTickerMarketCapNative * exchangeRate
      : singleTickerMarketCapNative);
  const marketCapDisplayCurrency = isWatchlistIndex || currency === 'USD' || (showUSD && hasExchangeRate)
    ? 'USD'
    : currency;
  const marketCapSource = isWatchlistIndex
    ? 'computed (sum of constituents)'
    : (Number.isFinite(quoteMarketCap) && quoteMarketCap > 0
      ? (quoteDetails?.source === 'Ghana Stock Exchange' ? 'GSE close × issued shares' : 'market quote')
      : (Number.isFinite(overviewMarketCap) && overviewMarketCap > 0
        ? 'company overview'
        : (adrRatio > 1 ? 'computed (ADR adjusted)' : 'computed')));

  const fundamentalsInUsd = currency === 'USD' || (showUSD && currency !== 'USD' && hasExchangeRate);

  const fundamentalsSnapshotData = (() => {
    const toDisplayFinancial = (value) => {
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return null;
      if (fundamentalsInUsd && currency !== 'USD') {
        return numericValue * exchangeRate;
      }
      return numericValue;
    };

    const formatNativeValue = (value) => {
      const symbol = getCurrencySymbol(currency);
      const absValue = Math.abs(value);
      if (absValue >= 1e12) return `${symbol}${(value / 1e12).toFixed(2)}T`;
      if (absValue >= 1e9) return `${symbol}${(value / 1e9).toFixed(2)}B`;
      if (absValue >= 1e6) return `${symbol}${(value / 1e6).toFixed(2)}M`;
      if (absValue >= 1e3) return `${symbol}${(value / 1e3).toFixed(2)}K`;
      return `${symbol}${value.toFixed(2)}`;
    };

    const revenueValue = toDisplayFinancial(data.latestMetrics?.revenue);
    const operatingIncomeValue = toDisplayFinancial(data.latestMetrics?.operating_income);
    const marketCapValue = Number.isFinite(Number(marketCap)) ? Number(marketCap) : null;

    const rows = [
      {
        key: 'marketCap',
        label: 'Market Cap',
        value: marketCapValue,
        color: '#3b82f6',
      },
      {
        key: 'revenue',
        label: 'Revenue',
        value: revenueValue,
        color: '#14b8a6',
      },
      {
        key: 'operatingIncome',
        label: 'Operating Income',
        value: operatingIncomeValue,
        color: Number(operatingIncomeValue) >= 0 ? '#22c55e' : '#ef4444',
      },
    ].filter((entry) => Number.isFinite(entry.value));

    const maxAbsValue = rows.length
      ? Math.max(...rows.map((entry) => Math.abs(Number(entry.value))))
      : 0;
    const marketCapAnchor = Number.isFinite(marketCapValue) && Math.abs(marketCapValue) > 0
      ? Math.abs(marketCapValue)
      : maxAbsValue;

    return rows.map((entry) => {
      const rawValue = Number(entry.value);
      const relativeToMarketCapPct = marketCapAnchor > 0
        ? (rawValue / marketCapAnchor) * 100
        : 0;
      const scaledValue = Number.isFinite(relativeToMarketCapPct) ? relativeToMarketCapPct : 0;
      const formattedValue = entry.key === 'marketCap' || fundamentalsInUsd
        ? formatUSD(rawValue)
        : formatNativeValue(rawValue);

      return {
        ...entry,
        scaledValue,
        formattedValue,
        relativePct: relativeToMarketCapPct,
        relativeLabel: `${relativeToMarketCapPct.toFixed(2)}% of market cap`,
        valueAndPercentLabel: `${formattedValue} (${relativeToMarketCapPct.toFixed(2)}%)`,
      };
    });
  })();
  
  // Check if financial data currency matches stock currency (USD for US-listed stocks)
  const isCurrencyMismatch = currency !== 'USD';
  
  // Detect reporting cadence while ignoring likely cumulative year-end rows.
  const detectDataFrequency = () => {
    if (data.financialData.length < 2) return 'unknown';

    const cadenceRows = data.financialData.filter((row, index) => {
      if (index === 0) return true;
      return !isLikelyCumulativeAnnualRow(row, data.financialData[index - 1]);
    });
    if (cadenceRows.length < 2) return 'unknown';

    const hasCumulativeAnnualMarkers = data.financialData.some((row, index) => (
      index > 0 && isLikelyCumulativeAnnualRow(row, data.financialData[index - 1])
    ));

    const periods = cadenceRows.map(d => new Date(d.period).getTime());
    const gaps = [];
    for (let i = 1; i < periods.length; i++) {
      gaps.push(periods[i] - periods[i - 1]);
    }
    const avgGapDays = gaps.reduce((a, b) => a + b, 0) / gaps.length / (1000 * 60 * 60 * 24);

    if (avgGapDays > 260) return hasCumulativeAnnualMarkers ? 'semiannual' : 'annual';
    if (avgGapDays > 140) return 'semiannual';
    return 'quarterly';
  };
  
  const dataFrequency = detectDataFrequency();
  const isAnnualData = dataFrequency === 'annual';
  const isSemiAnnualData = dataFrequency === 'semiannual';
  const cardUsesAnnualCadence = effectiveFinancialPeriodType === 'annual';
  const latestPeriodLabel = cardUsesAnnualCadence ? 'Latest Annual' : 'Latest Quarter';
  const previousPeriodLabel = cardUsesAnnualCadence ? 'year' : 'quarter';

  const valuationRows = data.financialData.filter((row, index) => {
    if (index === 0) return true;
    return !isLikelyCumulativeAnnualRow(row, data.financialData[index - 1]);
  });
  
  // Calculate annual operating income based on data frequency
  let annualOperatingIncome = 0;
  if (isAnnualData) {
    // For annual data, use the most recent year's operating income
    const latestPeriod = valuationRows[valuationRows.length - 1] || data.financialData[data.financialData.length - 1];
    annualOperatingIncome = getOperatingIncomeValue(latestPeriod) || 0;
  } else if (isSemiAnnualData) {
    // Annualize latest half-year income as best available estimate.
    const latestPeriod = valuationRows[valuationRows.length - 1] || data.financialData[data.financialData.length - 1];
    annualOperatingIncome = (getOperatingIncomeValue(latestPeriod) || 0) * 2;
  } else {
    // For quarterly data, sum the last 4 quarters (TTM)
    const last4Quarters = valuationRows.slice(-4);
    annualOperatingIncome = last4Quarters.reduce((sum, quarter) => {
      const quarterIncome = getOperatingIncomeValue(quarter);
      return sum + (Number.isFinite(quarterIncome) ? quarterIncome : 0);
    }, 0);
  }
  const hasAnnualOperatingIncome = annualOperatingIncome > 0;
  
  // Convert operating income to USD using exchange rate if available
  const annualOperatingIncomeUSD = hasAnnualOperatingIncome && hasExchangeRate
    ? annualOperatingIncome * exchangeRate
    : annualOperatingIncome;

  const latestIndexPoint = isWatchlistIndex && data.financialData.length
    ? data.financialData[data.financialData.length - 1]
    : null;
  const indexCoverageCount = Number(latestIndexPoint?.constituents) || 0;
  
  // Calculate earnings multiple (use converted USD value if exchange rate available)
  const canCalculateMultiple = marketCap && hasAnnualOperatingIncome && (!isCurrencyMismatch || hasExchangeRate);
  const operatingIncomeMultiple = canCalculateMultiple
    ? marketCap / annualOperatingIncomeUSD
    : null;

  const parsedForwardOperatingIncome = parseHumanReadableNumber(forwardOperatingIncomeInput);
  const hasForwardOperatingIncomeInput = Number.isFinite(parsedForwardOperatingIncome) && parsedForwardOperatingIncome > 0;
  const forwardOperatingIncomeUSD = hasForwardOperatingIncomeInput
    ? ((isCurrencyMismatch && hasExchangeRate)
      ? parsedForwardOperatingIncome * exchangeRate
      : parsedForwardOperatingIncome)
    : null;
  const forwardPeFromInput = marketCap && Number.isFinite(forwardOperatingIncomeUSD) && forwardOperatingIncomeUSD > 0
    ? marketCap / forwardOperatingIncomeUSD
    : null;

  const hasForwardPE = Number.isFinite(forwardPE) && forwardPE > 0;
  const displayedForwardPE = Number.isFinite(forwardPeFromInput) && forwardPeFromInput > 0
    ? forwardPeFromInput
    : (hasForwardPE ? forwardPE : null);

  const getPeValuationColor = (peValue) => {
    const pe = Number(peValue);
    if (!Number.isFinite(pe) || pe <= 0) return 'text.primary';
    if (pe <= 15) return 'success.main';
    if (pe <= 30) return 'text.primary';
    if (pe <= 45) return 'warning.main';
    return 'error.main';
  };

  const peQuickViewColor = getPeValuationColor(operatingIncomeMultiple);

  const resolveThemeColor = (token) => {
    const [section, shade] = String(token || '').split('.');
    if (!section || !shade) return theme.palette.text.primary;
    return theme.palette?.[section]?.[shade] || theme.palette.text.primary;
  };

  const accountingFontFamily = '"Roboto Mono", "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';

  const getFundamentalsSnapshotEntry = (key) => fundamentalsSnapshotData.find((entry) => entry.key === key);

  const selectedTicker = String(ticker || '').trim().toUpperCase();
  const nextEarningsForSelectedTicker = selectedTicker
    ? nextEarningsByTicker[selectedTicker] || null
    : null;

  const institutionalOwnershipChartData = useMemo(() => {
    const breakdown = Array.isArray(institutionalOwnership?.breakdown)
      ? institutionalOwnership.breakdown
      : [];
    const marketCapNumeric = Number.isFinite(Number(marketCap)) ? Number(marketCap) : null;

    return breakdown.map((entry) => {
      const percentage = Number(entry?.percentage);
      const amountUSD = Number.isFinite(percentage) && Number.isFinite(marketCapNumeric)
        ? marketCapNumeric * (percentage / 100)
        : null;
      return {
        ...entry,
        percentage,
        amountUSD,
        formattedAmount: Number.isFinite(amountUSD) ? formatUSD(amountUSD) : null,
      };
    });
  }, [institutionalOwnership?.breakdown, marketCap]);

  const etfSharesOutstanding = Number.isFinite(outstandingShares) && outstandingShares > 0
    ? outstandingShares
    : (Number.isFinite(Number(etfBalanceSnapshot?.sharesOutstanding)) && Number(etfBalanceSnapshot.sharesOutstanding) > 0
      ? Number(etfBalanceSnapshot.sharesOutstanding)
      : null);
  const etfNetAssets = Number.isFinite(Number(etfBalanceSnapshot?.netAssets))
    ? Number(etfBalanceSnapshot.netAssets)
    : null;
  const etfTotalAssets = Number.isFinite(Number(etfBalanceSnapshot?.assets))
    ? Number(etfBalanceSnapshot.assets)
    : null;
  const etfTotalLiabilities = Number.isFinite(Number(etfBalanceSnapshot?.liabilities))
    ? Number(etfBalanceSnapshot.liabilities)
    : null;
  const etfCash = Number.isFinite(Number(etfBalanceSnapshot?.cash))
    ? Number(etfBalanceSnapshot.cash)
    : null;
  const etfNavPerShare = Number.isFinite(Number(etfBalanceSnapshot?.navPerShare)) && Number(etfBalanceSnapshot.navPerShare) > 0
    ? Number(etfBalanceSnapshot.navPerShare)
    : (Number.isFinite(etfNetAssets) && Number.isFinite(etfSharesOutstanding) && etfSharesOutstanding > 0
      ? etfNetAssets / etfSharesOutstanding
      : null);
  const etfHoldingsCount = Number.isFinite(Number(etfBalanceSnapshot?.holdingsCount)) && Number(etfBalanceSnapshot.holdingsCount) >= 0
    ? Number(etfBalanceSnapshot.holdingsCount)
    : (etfHoldings.length || null);
  const etfPremiumDiscountPct = Number.isFinite(currentPrice) && Number.isFinite(etfNavPerShare) && etfNavPerShare > 0
    ? ((currentPrice - etfNavPerShare) / etfNavPerShare) * 100
    : null;

  const etfHoldingsChartData = useMemo(
    () => etfHoldings
      .filter((item) => Number.isFinite(item?.weight) && item.weight > 0)
      .slice(0, 10)
      .map((item) => ({
        name: item.symbol,
        fullName: item.name,
        weightPct: item.weight * 100,
      })),
    [etfHoldings]
  );

  if (loading) {
    return (
      <Box sx={{ p: { xs: 0, sm: 1 } }}>
        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
            <Box sx={{ minWidth: 240, flex: 1 }}>
              <Skeleton variant="text" width="40%" height={48} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="30%" height={26} />
              <Skeleton variant="rounded" width={220} height={56} sx={{ mt: 1.5 }} />
            </Box>
            <Box display="flex" gap={1}>
              <Skeleton variant="rounded" width={92} height={32} />
              <Skeleton variant="rounded" width={96} height={32} />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Box display="flex" alignItems="center" mb={1.5}>
              <InfoOutlinedIcon color="disabled" sx={{ mr: 1 }} />
              <Skeleton variant="text" width={180} height={30} />
            </Box>
            <Grid container spacing={2}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <Grid item xs={12} sm={6} md={3} key={`facts-skeleton-${idx}`}>
                  <Skeleton variant="text" width="65%" height={20} />
                  <Skeleton variant="text" width="85%" height={30} />
                </Grid>
              ))}
              <Grid item xs={12}>
                <Skeleton variant="text" width="20%" height={20} />
                <Skeleton variant="text" width="90%" height={28} />
              </Grid>
            </Grid>
          </Box>
        </Paper>

        <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, height: { xs: 320, sm: 400 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Skeleton variant="text" width={300} height={34} />
            <Skeleton variant="rounded" width={160} height={32} />
          </Box>
          <Skeleton variant="rounded" width="100%" height="78%" />
        </Paper>

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
          {Array.from({ length: 6 }).map((_, idx) => (
            <Grid item xs={12} sm={6} md={4} key={`card-skeleton-${idx}`}>
              <Card elevation={2}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Skeleton variant="text" width="58%" height={22} />
                  <Skeleton variant="text" width="42%" height={46} sx={{ my: 0.5 }} />
                  <Skeleton variant="text" width="72%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 1 }, display: 'flex', flexDirection: 'column' }}>
      {/* Company Overview */}
      <Grid container spacing={3} sx={{ mb: 3, order: 1 }} alignItems="flex-start">
      <Grid item xs={12} md={(isWatchlistIndex || isEtfDashboard) ? 12 : 8}>
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={{ xs: 1.5, md: 2 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h4" fontWeight="500" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' }, lineHeight: 1.15 }}>
              {data.companyName?.company_name || data.companyOverview?.company_name || ticker}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary" sx={{ fontSize: { xs: '0.92rem', sm: '1rem' }, mt: 0.5 }}>
              {isWatchlistIndex
                ? `${indexTickers.length} constituents • Composite Index`
                : `${ticker} • ${data.companyOverview?.exchange || exchange}`}
            </Typography>
          </Box>
          <FormControl size="small" sx={{ minWidth: 200, width: { xs: '100%', md: 'auto' } }}>
            <Select
              value={defaultExplainerStyle}
              onChange={(event) => {
                const value = String(event.target.value || EXPLAINER_STYLE_CONVERSATIONAL);
                setDefaultExplainerStyle(
                  value === EXPLAINER_STYLE_ADVANCED
                    ? EXPLAINER_STYLE_ADVANCED
                    : EXPLAINER_STYLE_CONVERSATIONAL
                );
              }}
            >
              <MenuItem value={EXPLAINER_STYLE_CONVERSATIONAL}>Explainers: Conversational (default)</MenuItem>
              <MenuItem value={EXPLAINER_STYLE_ADVANCED}>Explainers: Advanced (default)</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
          <Chip 
            label={data.companyOverview?.sector || 'N/A'} 
            color="primary" 
            variant="outlined"
          />
          {(() => {
            const qualityChip = getDataQualityChip(data.financialDataQuality);
            return (
              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                <Chip
                  label={qualityChip.label}
                  color={qualityChip.color}
                  variant="outlined"
                />
                {renderDataQualityInfo()}
              </Box>
            );
          })()}
          {!isEtfDashboard && data.latestMetrics?.profit_margin > 10 && (
            <Chip 
              label="Profitable" 
              color="success" 
              variant="outlined"
            />
          )}
        </Box>

        {isWatchlistIndex ? (
          <Box
            sx={{
              mt: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.9,
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: theme.palette.warning.main,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.14)' : 'rgba(245, 158, 11, 0.1)',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 800, color: theme.palette.warning.main }}>
              Upcoming earnings reporting calls
            </Typography>
            {earningsLoading ? (
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Loading upcoming earnings...
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {indexTickers.map((symbol) => {
                  const nextEvent = nextEarningsByTicker[symbol] || null;
                  return (
                    <Chip
                      key={symbol}
                      size="small"
                      color={nextEvent ? 'warning' : 'default'}
                      variant={nextEvent ? 'filled' : 'outlined'}
                      sx={{ fontWeight: 700 }}
                      label={nextEvent
                        ? `${symbol}: ${formatEarningsDate(nextEvent.date)} • ${formatEarningsTime(nextEvent.time)}`
                        : `${symbol}: Not scheduled`}
                    />
                  );
                })}
              </Box>
            )}
          </Box>
        ) : isEtfDashboard ? (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: theme.palette.info.main,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(56, 189, 248, 0.14)' : 'rgba(56, 189, 248, 0.1)',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, letterSpacing: '0.04em', color: theme.palette.info.main }}>
              ETF SNAPSHOT
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700, color: 'text.primary' }}>
              {priceLoading
                ? 'Loading quote...'
                : (Number.isFinite(currentPrice) && currentPrice > 0
                  ? `Market Price: ${currentPrice.toFixed(2)} ${data.companyOverview?.currency || ''}`.trim()
                  : 'Market price unavailable')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {etfHoldingsLoading
                ? 'Loading holdings composition...'
                : `${etfHoldings.length} holdings loaded${etfHoldingsSource ? ` • Source: ${etfHoldingsSource}` : ''}`}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              mt: 1.5,
              p: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: theme.palette.warning.main,
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(245, 158, 11, 0.14)' : 'rgba(245, 158, 11, 0.1)',
            }}
          >
            <Typography variant="caption" sx={{ display: 'block', fontWeight: 800, letterSpacing: '0.04em', color: theme.palette.warning.main }}>
              NEXT EARNINGS CALL
            </Typography>
            <Typography variant="body1" sx={{ mt: 0.35, fontWeight: 700, color: 'text.primary' }}>
              {earningsLoading
                ? 'Loading...'
                : nextEarningsForSelectedTicker
                  ? `${formatEarningsDate(nextEarningsForSelectedTicker.date)} • ${formatEarningsTime(nextEarningsForSelectedTicker.time)}`
                  : 'Not currently scheduled'}
            </Typography>
          </Box>
        )}
        {data.companyOverview?.description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            {data.companyOverview.description}
          </Typography>
        )}
        <Divider sx={{ my: 2 }} />
        {cadenceNotice && (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="info.main" sx={{ fontWeight: 700 }}>
              {cadenceNotice}
            </Typography>
          </Box>
        )}
        {Array.isArray(data.financialDataQuality?.warnings) && data.financialDataQuality.warnings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="warning.main" sx={{ fontWeight: 700 }}>
              {data.financialDataQuality.warnings[0]}
            </Typography>
          </Box>
        )}
        <Box>
          <Box display="flex" alignItems="center" mb={0.75}>
            <InfoOutlinedIcon color="primary" sx={{ mr: 0.75, fontSize: 19 }} />
            <Typography variant="h6">{isWatchlistIndex ? 'Index Quick Facts' : (isEtfDashboard ? 'ETF Quick Facts' : 'Company Quick Facts')}</Typography>
          </Box>

          {isWatchlistIndex ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Index Type</Typography>
                <Typography variant="body1">Watchlist Composite</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Constituents</Typography>
                <Typography variant="body1">{indexTickers.length}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Latest Period Coverage</Typography>
                <Typography variant="body1">{indexCoverageCount}/{indexTickers.length}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Market Cap ({marketCapDisplayCurrency})</Typography>
                <Typography variant="body1">
                  {marketCap ? (marketCapDisplayCurrency === 'USD' ? formatUSD(marketCap) : formatCurrency(marketCap)) : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Base Currency</Typography>
                <Typography variant="body1">USD</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Dominant Sector</Typography>
                <Typography variant="body1">{data.companyOverview?.industry || 'Mixed'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Index Constituents</Typography>
                <Typography variant="body1">{indexTickers.join(', ') || 'N/A'}</Typography>
              </Grid>
            </Grid>
          ) : isEtfDashboard ? (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Net Assets (USD)</Typography>
                <Typography variant="body1">{Number.isFinite(etfNetAssets) ? formatUSD(etfNetAssets) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total Assets (USD)</Typography>
                <Typography variant="body1">{Number.isFinite(etfTotalAssets) ? formatUSD(etfTotalAssets) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Total Liabilities (USD)</Typography>
                <Typography variant="body1">{Number.isFinite(etfTotalLiabilities) ? formatUSD(etfTotalLiabilities) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Cash & Equivalents (USD)</Typography>
                <Typography variant="body1">{Number.isFinite(etfCash) ? formatUSD(etfCash) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Market Price</Typography>
                <Typography variant="body1">
                  {priceLoading
                    ? 'Loading...'
                    : (Number.isFinite(currentPrice) && currentPrice > 0
                      ? `${currentPrice.toFixed(2)} ${data.companyOverview?.currency || ''}`.trim()
                      : 'N/A')}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">NAV / Share (Proxy)</Typography>
                <Typography variant="body1">{Number.isFinite(etfNavPerShare) ? formatUSD(etfNavPerShare) : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Premium / Discount vs NAV</Typography>
                <Typography variant="body1" color={Number(etfPremiumDiscountPct) >= 0 ? 'success.main' : 'error.main'}>
                  {Number.isFinite(etfPremiumDiscountPct)
                    ? `${etfPremiumDiscountPct >= 0 ? '+' : ''}${etfPremiumDiscountPct.toFixed(2)}%`
                    : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Holdings Count</Typography>
                <Typography variant="body1">{Number.isFinite(etfHoldingsCount) ? etfHoldingsCount : 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Shares Outstanding</Typography>
                <Typography variant="body1">
                  {Number.isFinite(etfSharesOutstanding) ? Number(etfSharesOutstanding).toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Category</Typography>
                <Typography variant="body1">{data.companyOverview?.industry || data.companyOverview?.sector || 'ETF'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">Country/Currency</Typography>
                <Typography variant="body1">{data.companyOverview?.country || 'N/A'}/{data.companyOverview?.currency || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Latest Filing Snapshot</Typography>
                <Typography variant="body1">
                  {etfBalanceSnapshot?.period
                    ? `${etfBalanceSnapshot.sourceForm || 'Fund filing'} period: ${new Date(etfBalanceSnapshot.period).toLocaleDateString()}`
                    : 'No ETF filing snapshot available yet.'}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                backgroundColor: 'transparent',
                boxShadow: 'none',
                borderColor: theme.palette.divider,
                borderRadius: 1.5,
                overflow: 'hidden',
              }}
            >
              <Table
                size="small"
                sx={{
                  tableLayout: 'fixed',
                  '& .MuiTableCell-root': {
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    py: 0.85,
                    px: { xs: 1.25, sm: 1.5 },
                  },
                  '& .MuiTableRow-root:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <TableBody>
                  {[
                    {
                      label: `Market Cap (${marketCapDisplayCurrency})`,
                      value: marketCap ? (marketCapDisplayCurrency === 'USD' ? formatUSD(marketCap) : formatCurrency(marketCap)) : 'N/A',
                      note: marketCap ? `Source: ${marketCapSource}` : 'Unavailable',
                      noteColor: 'text.secondary',
                      barKey: 'marketCap',
                    },
                    {
                      label: `Revenue (${latestPeriodLabel})`,
                      value: formatWithConversion(data.latestMetrics?.revenue || 0, showUSD),
                      note: data.previousMetrics ? `${getPercentChange(data.latestMetrics?.revenue || 0, data.previousMetrics?.revenue || 0).value}% vs previous ${previousPeriodLabel}` : null,
                      noteColor: getPercentChange(data.latestMetrics?.revenue || 0, data.previousMetrics?.revenue || 0).isPositive ? 'success.main' : 'error.main',
                      barKey: 'revenue',
                    },
                    {
                      label: `Operating Income (${latestPeriodLabel})`,
                      value: formatWithConversion(data.latestMetrics?.operating_income || 0, showUSD),
                      note: data.previousMetrics ? `${getPercentChange(data.latestMetrics?.operating_income || 0, data.previousMetrics?.operating_income || 0).value}% vs previous ${previousPeriodLabel}` : null,
                      noteColor: getPercentChange(data.latestMetrics?.operating_income || 0, data.previousMetrics?.operating_income || 0).isPositive ? 'success.main' : 'error.main',
                      barKey: 'operatingIncome',
                    },
                    {
                      label: 'Profit Margin',
                      value: `${(data.latestMetrics?.profit_margin || 0).toFixed(2)}%`,
                      note: data.previousMetrics ? `${getPercentChange(data.latestMetrics?.profit_margin || 0, data.previousMetrics?.profit_margin || 0).value}% vs previous ${previousPeriodLabel}` : null,
                      noteColor: getPercentChange(data.latestMetrics?.profit_margin || 0, data.previousMetrics?.profit_margin || 0).isPositive ? 'success.main' : 'error.main',
                    },
                    {
                      label: 'Sector',
                      value: data.companyOverview?.sector || 'N/A',
                    },
                    {
                      label: 'Industry',
                      value: data.companyOverview?.industry || 'N/A',
                    },
                    {
                      label: 'Current Share Price',
                      value: priceLoading ? 'Loading...' : (Number.isFinite(currentPrice) && currentPrice > 0 ? `${currentPrice.toFixed(2)} ${data.companyOverview?.currency || ''}`.trim() : 'N/A'),
                      note: [
                        quoteStatusMessage || (priceLastUpdated ? `Updated ${formatTimeInAppTimeZone(priceLastUpdated)}` : 'Live price feed unavailable'),
                        quoteDetails?.source === 'Ghana Stock Exchange'
                          ? `52-week range ${Number.isFinite(Number(quoteDetails.yearLow)) ? Number(quoteDetails.yearLow).toFixed(2) : '-'}-${Number.isFinite(Number(quoteDetails.yearHigh)) ? Number(quoteDetails.yearHigh).toFixed(2) : '-'} GHS · Bid ${Number.isFinite(Number(quoteDetails.bid)) ? Number(quoteDetails.bid).toFixed(2) : '-'} · Offer ${Number.isFinite(Number(quoteDetails.offer)) ? Number(quoteDetails.offer).toFixed(2) : '-'} · Volume ${Number.isFinite(Number(quoteDetails.volume)) ? Number(quoteDetails.volume).toLocaleString() : '-'}`
                          : null,
                      ].filter(Boolean),
                      noteColor: 'text.secondary',
                    },
                    {
                      label: 'P/E Valuation (Quick View)',
                      value: operatingIncomeMultiple ? `${operatingIncomeMultiple.toFixed(2)}x` : (isCurrencyMismatch && !hasExchangeRate ? 'Loading...' : 'N/A'),
                      valueColor: resolveThemeColor(peQuickViewColor),
                      note: operatingIncomeMultiple
                        ? `Proxy: Market Cap / Operating Income (${isAnnualData ? 'Annual' : (isSemiAnnualData ? 'Annualized Half-Year' : 'TTM')})`
                        : (isCurrencyMismatch && !hasExchangeRate ? 'Converting to USD...' : (!marketCap ? 'Market cap unavailable' : 'Not meaningful (trailing operating income is break-even or negative)')),
                      noteColor: 'text.secondary',
                    },
                    {
                      label: 'Forward P/E',
                      value: displayedForwardPE ? `${displayedForwardPE.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x` : 'N/A',
                      note: hasForwardOperatingIncomeInput
                        ? 'Manual forward operating income input'
                        : (hasForwardPE ? 'Source: Yahoo estimate' : 'No forward estimate available'),
                      noteColor: 'text.secondary',
                      customValue: (
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {displayedForwardPE ? `${displayedForwardPE.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}x` : 'N/A'}
                          </Typography>
                          <TextField
                            size="small"
                            fullWidth
                            type="text"
                            value={forwardOperatingIncomeInput}
                            onChange={(event) => setForwardOperatingIncomeInput(event.target.value)}
                            onBlur={() => {
                              const parsedValue = parseHumanReadableNumber(forwardOperatingIncomeInput);
                              if (Number.isFinite(parsedValue) && parsedValue > 0) {
                                setForwardOperatingIncomeInput(formatAccountingNumber(parsedValue));
                              }
                            }}
                            label={`Forward Operating Income (${currency})`}
                            placeholder="1.25B, 850M, 1250000000"
                            inputProps={{ inputMode: 'decimal' }}
                            sx={{ mt: 0.5 }}
                          />
                          {hasForwardOperatingIncomeInput ? (
                            <Typography variant="caption" color="info.main" sx={{ display: 'block', mt: 0.5 }}>
                              Using manual value: {getCurrencySymbol(currency)}{formatAccountingNumber(parsedForwardOperatingIncome)} (~{formatCompactNumber(parsedForwardOperatingIncome)})
                            </Typography>
                          ) : null}
                        </Box>
                      ),
                    },
                    {
                      label: 'Country/Currency',
                      value: `${data.companyOverview?.country || 'N/A'} / ${data.companyOverview?.currency || 'N/A'}`,
                    },
                    {
                      label: 'Website',
                      value: data.companyOverview?.official_site ? (
                        <a href={data.companyOverview.official_site} target="_blank" rel="noopener noreferrer">
                          {data.companyOverview.official_site}
                        </a>
                      ) : (data.companyOverview?.website_search_url ? (
                        <a href={data.companyOverview.website_search_url} target="_blank" rel="noopener noreferrer">
                          Find Website
                        </a>
                      ) : 'N/A'),
                    },
                    {
                      label: 'Address',
                      value: data.companyOverview?.address || 'N/A',
                    },
                  ].map((row) => (
                    <TableRow key={row.label} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                      <TableCell component="th" scope="row" sx={{ width: '36%', fontSize: { xs: '0.72rem', sm: '0.78rem' }, fontWeight: 700, lineHeight: 1.25, color: 'text.primary', whiteSpace: { xs: 'normal', sm: 'nowrap' }, verticalAlign: 'top' }}>
                        {row.label}
                      </TableCell>
                      <TableCell sx={{ color: 'text.primary', verticalAlign: 'top' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.15, minWidth: 0 }}>
                          {row.customValue || <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3, color: row.valueColor || 'text.primary', wordBreak: 'break-word' }}>{row.value}</Typography>}
                          {Array.isArray(row.note) ? row.note.map((note) => (
                            <Typography key={note} variant="caption" color={row.noteColor || 'text.secondary'} sx={{ fontSize: '0.67rem', lineHeight: 1.2 }}>
                              {note}
                            </Typography>
                          )) : row.note ? (
                            <Typography variant="caption" color={row.noteColor || 'text.secondary'} sx={{ fontSize: '0.67rem', lineHeight: 1.2 }}>
                              {row.note}
                            </Typography>
                          ) : null}
                          {row.barKey && (() => {
                            const entry = getFundamentalsSnapshotEntry(row.barKey);
                            if (!entry) return null;
                            const barWidthPct = entry.key === 'marketCap'
                              ? 100
                              : Math.max(3, Math.min(100, Math.abs(entry.relativePct)));
                            const isNegative = Number(entry.relativePct) < 0;
                            return (
                              <>
                                <Box
                                  sx={{
                                    position: 'relative',
                                    height: 5,
                                    mt: 0.35,
                                    borderRadius: 4,
                                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(148,163,184,0.15)' : 'rgba(15,23,42,0.08)',
                                    overflow: 'hidden',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      height: '100%',
                                      width: `${barWidthPct}%`,
                                      borderRadius: 4,
                                      bgcolor: entry.color,
                                      ...(isNegative ? { right: 0 } : { left: 0 }),
                                    }}
                                  />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', lineHeight: 1.15 }}>
                                  {entry.relativeLabel}
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

      </Paper>
      </Grid>

      {!isWatchlistIndex && !isEtfDashboard && (
        <Grid item xs={12} md={4}>
          <Paper
            elevation={2}
            sx={{
              p: { xs: 1.5, sm: 2 },
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 0.25 }}>
                  Institutional Ownership
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Share ownership from aggregated institutional 13F filings
                </Typography>
              </Box>
              {institutionalOwnership?.asOfDate ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`As of ${new Date(institutionalOwnership.asOfDate).toLocaleDateString()}`}
                />
              ) : null}
            </Box>

            {institutionalOwnershipLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
                <CircularProgress size={26} />
              </Box>
            ) : (institutionalOwnershipChartData.length > 0) ? (
              <>
                <ResponsiveContainer width="100%" height={isMobile ? 240 : 260}>
                  <PieChart margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                    <Pie
                      data={institutionalOwnershipChartData}
                      dataKey="percentage"
                      nameKey="label"
                      cx="50%"
                      cy="50%"
                      outerRadius={isMobile ? 76 : 90}
                      innerRadius={isMobile ? 42 : 52}
                      paddingAngle={2}
                      labelLine={false}
                      label={({ percentage }) => `${Number(percentage || 0).toFixed(1)}%`}
                    >
                      {institutionalOwnershipChartData.map((entry) => {
                        const colorByKey = {
                          institutions: '#2563eb',
                          insiders: '#f59e0b',
                          others: '#10b981',
                        };
                        return <Cell key={`ownership-cell-${entry.key}`} fill={colorByKey[entry.key] || '#64748b'} />;
                      })}
                    </Pie>
                    <Tooltip formatter={(value, _name, meta) => {
                      const payload = meta?.payload || {};
                      return [
                        `${Number(value || 0).toFixed(2)}%${payload.formattedAmount ? ` (${payload.formattedAmount})` : ''}`,
                        payload.label || 'Ownership',
                      ];
                    }} />
                  </PieChart>
                </ResponsiveContainer>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                  {institutionalOwnershipChartData.map((entry) => (
                    <Chip
                      key={`ownership-chip-${entry.key}`}
                      size="small"
                      label={`${entry.label}: ${Number(entry.percentage || 0).toFixed(2)}%${entry.formattedAmount ? ` (${entry.formattedAmount})` : ''}`}
                      variant="outlined"
                      sx={{ justifyContent: 'flex-start', width: 'fit-content', maxWidth: '100%' }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                Institutional ownership data is currently unavailable for this ticker.
              </Typography>
            )}
          </Paper>

        </Grid>
      )}
      </Grid>

      {!isEtfDashboard && (
      <>
      {/* Revenue and Income Trend Chart */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 3,
          height: { xs: 440, sm: 560, md: 620 },
          order: 2,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography variant="h6" sx={{ mb: 0 }}>Revenue & Operating Income/Loss Trends</Typography>
            {chartData.some((point) => point.isIpoPeriod) ? (
              <Chip
                size="small"
                label={
                  chartData.find((point) => point.isIpoPeriod)?.ipoLabel
                  || getIpoPeriodLabel(chartData.find((point) => point.isIpoPeriod)?.sourceForm)
                }
                sx={{
                  bgcolor: 'rgba(245, 158, 11, 0.18)',
                  color: '#f59e0b',
                  border: '1px solid rgba(245, 158, 11, 0.55)',
                  fontWeight: 800,
                  letterSpacing: 0.2,
                }}
              />
            ) : null}
            {chartData.some((point) => point.isPreliminary) ? (
              <Chip
                size="small"
                label={
                  chartData.find((point) => point.isPreliminary)?.preliminaryLabel
                  || getPreliminary8kLabel()
                }
                sx={{
                  bgcolor: 'rgba(6, 182, 212, 0.18)',
                  color: '#06b6d4',
                  border: '1px solid rgba(6, 182, 212, 0.6)',
                  fontWeight: 800,
                  letterSpacing: 0.2,
                }}
              />
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={financialPeriodType}
              onChange={(_, nextType) => {
                if (nextType) setFinancialPeriodType(nextType);
              }}
            >
              <ToggleButton value="quarterly">Quarterly</ToggleButton>
              <ToggleButton value="annual">Annual</ToggleButton>
            </ToggleButtonGroup>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={chartZoomYears}
              onChange={(_, nextZoom) => {
                if (nextZoom !== null) setChartZoomYears(nextZoom);
              }}
            >
              <ToggleButton value={1}>1Y</ToggleButton>
              <ToggleButton value={2}>2Y</ToggleButton>
              <ToggleButton value={3}>3Y</ToggleButton>
              <ToggleButton value={5}>5Y</ToggleButton>
              <ToggleButton value={10}>10Y</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          {(() => {
            // Target a comfortable number of visible X-axis labels based on viewport.
            // Quarterly data over 10Y = ~40 ticks, which collide if we render them all.
            const targetTickCount = isMobile ? 4 : isTablet ? 6 : 10;
            const step = Math.max(1, Math.ceil((chartData.length || 1) / targetTickCount));
            // Anchor the thinning to the END so the latest reporting period is
            // always rendered, then walk backwards by `step`. This guarantees the
            // most recent quarter is the right-most label on every zoom level.
            const xTicks = [];
            if (chartData.length > 0) {
              for (let i = chartData.length - 1; i >= 0; i -= step) {
                xTicks.unshift(chartData[i].period);
              }
              // Always keep IPO / preliminary period labels visible even when ticks are thinned.
              chartData.forEach((point) => {
                if ((point?.isIpoPeriod || point?.isPreliminary) && point.period && !xTicks.includes(point.period)) {
                  xTicks.push(point.period);
                }
              });
              xTicks.sort((a, b) => {
                const aIdx = chartData.findIndex((d) => d.period === a);
                const bIdx = chartData.findIndex((d) => d.period === b);
                return aIdx - bIdx;
              });
              // Drop the first selected tick if it would visually crowd the second
              // one (happens when chartData.length isn't a clean multiple of step).
              if (xTicks.length > 1) {
                const firstIdx = chartData.findIndex((d) => d.period === xTicks[0]);
                const secondIdx = chartData.findIndex((d) => d.period === xTicks[1]);
                if (
                  secondIdx - firstIdx < Math.ceil(step / 2)
                  && !chartData[firstIdx]?.isIpoPeriod
                  && !chartData[firstIdx]?.isPreliminary
                ) {
                  xTicks.shift();
                }
              }
            }
            // Rotate labels once there are more than ~8 points so they don't overlap.
            const xTickAngle = chartData.length > 8 ? (isMobile ? -45 : -30) : 0;
            const hasIpoTick = chartData.some((point) => point.isIpoPeriod);
            const hasPrelimTick = chartData.some((point) => point.isPreliminary);
            const bottomMargin = xTickAngle !== 0
              ? (isMobile ? 58 : 68)
              : (isMobile ? 40 : 48);
            const ipoAccent = '#f59e0b';
            const prelimAccent = '#06b6d4';
            const renderPeriodTick = (tickProps) => {
              const { x, y, payload } = tickProps;
              const label = payload?.value;
              const point = chartData.find((row) => row.period === label);
              const isIpo = Boolean(point?.isIpoPeriod);
              const isPrelim = Boolean(point?.isPreliminary);
              const fill = isIpo ? ipoAccent : (isPrelim ? prelimAccent : theme.palette.text.secondary);
              const fontWeight = (isIpo || isPrelim) ? 800 : 500;
              const fontSize = (isIpo || isPrelim) ? (isMobile ? 10 : 11) : (isMobile ? 9 : 10);
              return (
                <g transform={`translate(${x},${y})`}>
                  <text
                    dy={xTickAngle !== 0 ? 8 : 12}
                    textAnchor={xTickAngle !== 0 ? 'end' : 'middle'}
                    transform={xTickAngle !== 0 ? `rotate(${xTickAngle})` : undefined}
                    fill={fill}
                    fontSize={fontSize}
                    fontWeight={fontWeight}
                  >
                    {label}
                  </text>
                  {isIpo ? (
                    <title>{`${point?.ipoLabel || getIpoPeriodLabel(point?.sourceForm)} period`}</title>
                  ) : null}
                  {isPrelim && !isIpo ? (
                    <title>{`${point?.preliminaryLabel || getPreliminary8kLabel()} (not yet in 10-Q/10-K)`}</title>
                  ) : null}
                </g>
              );
            };
            return (
          <AreaChart data={chartData} margin={{ top: 10, right: isMobile ? 8 : 30, left: isMobile ? 4 : 24, bottom: bottomMargin }}>
            <defs>
              <linearGradient id="revenueAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#8884d8" stopOpacity={0.34} />
                <stop offset="95%" stopColor="#8884d8" stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="operatingIncomeAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.03} />
              </linearGradient>
              <linearGradient id="netIncomeAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.28} />
                <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
            <XAxis 
              dataKey="period" 
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
              label={isMobile ? undefined : {
                value: data.companyName?.company_name || data.companyOverview?.company_name || ticker,
                position: "bottom",
                offset: xTickAngle !== 0 ? 34 : 18,
                fontSize: 14,
                fontWeight: "bold",
                fill: theme.palette.text.secondary
              }}
              tick={renderPeriodTick}
              ticks={xTicks.length > 0 ? xTicks : undefined}
              interval={0}
              angle={xTickAngle}
              textAnchor={xTickAngle !== 0 ? 'end' : 'middle'}
              height={xTickAngle !== 0 ? (isMobile ? 50 : 56) : 30}
              minTickGap={isMobile ? 6 : 12}
            />
            <YAxis
              width={isMobile ? 64 : 96}
              tickMargin={8}
              axisLine={{ stroke: theme.palette.divider }}
              tickLine={{ stroke: theme.palette.divider }}
              tick={{ fill: theme.palette.text.primary, fontSize: isMobile ? 11 : 14 }}
              tickFormatter={(value) => formatChartCurrency(value)}
            />
            <Tooltip 
              formatter={(value) => formatChartCurrency(value)}
              labelFormatter={(period, payload) => {
                const companyName = data.companyName?.company_name || data.companyOverview?.company_name || ticker;
                const point = payload?.[0]?.payload;
                if (point?.isIpoPeriod) {
                  return `${companyName} - ${period} (IPO registration)`;
                }
                if (point?.isPreliminary) {
                  return `${companyName} - ${period} (Preliminary 8-K)`;
                }
                return `${companyName} - ${period}`;
              }}
              contentStyle={{
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17, 24, 39, 0.96)' : '#ffffff',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
              }}
              labelStyle={{
                color: theme.palette.mode === 'dark' ? '#f3f4f6' : '#111827',
                fontWeight: 600,
              }}
              itemStyle={{
                color: theme.palette.mode === 'dark' ? '#e5e7eb' : '#1f2937',
              }}
            />
            <Area
              type="monotone"
              dataKey="Revenue"
              name="Revenue"
              stroke="#8884d8"
              strokeWidth={3}
              fill="url(#revenueAreaFill)"
              fillOpacity={1}
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (cx == null || cy == null) return null;
                if (payload?.isIpoPeriod) {
                  return (
                    <g key={`ipo-rev-${payload.period}`}>
                      <circle cx={cx} cy={cy} r={8} fill={ipoAccent} fillOpacity={0.25} />
                      <circle cx={cx} cy={cy} r={5.5} fill={ipoAccent} stroke="#111827" strokeWidth={1.5} />
                    </g>
                  );
                }
                if (payload?.isPreliminary) {
                  return (
                    <g key={`prelim-rev-${payload.period}`}>
                      <circle cx={cx} cy={cy} r={9} fill={prelimAccent} fillOpacity={0.22} />
                      <circle cx={cx} cy={cy} r={5.5} fill={prelimAccent} stroke="#0f172a" strokeWidth={1.75} />
                    </g>
                  );
                }
                return <circle cx={cx} cy={cy} r={3} fill="#8884d8" stroke="white" strokeWidth={1} />;
              }}
              activeDot={{ r: 6 }}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="operatingIncomeValue"
              name="Operating Income"
              stroke="#82ca9d"
              strokeWidth={3}
              fill="url(#operatingIncomeAreaFill)"
              fillOpacity={1}
              dot={(props) => {
                const { cx, cy, payload } = props;
                const value = payload?.operatingIncomeValue;
                if (value === null || value === undefined) return null;
                if (payload?.isIpoPeriod) {
                  return (
                    <g key={`ipo-op-${payload.period}`}>
                      <circle cx={cx} cy={cy} r={8} fill={ipoAccent} fillOpacity={0.25} />
                      <circle cx={cx} cy={cy} r={5.5} fill={ipoAccent} stroke="#111827" strokeWidth={1.5} />
                    </g>
                  );
                }
                if (payload?.isPreliminary) {
                  return (
                    <g key={`prelim-op-${payload.period}`}>
                      <circle cx={cx} cy={cy} r={9} fill={prelimAccent} fillOpacity={0.22} />
                      <circle cx={cx} cy={cy} r={5.5} fill={prelimAccent} stroke="#0f172a" strokeWidth={1.75} />
                    </g>
                  );
                }
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill="#82ca9d"
                    stroke="white"
                    strokeWidth={1.5}
                  />
                );
              }}
              isAnimationActive={false}
            />
            <Area
              type="monotone"
              dataKey="netIncomeValue"
              name="Net Income"
              stroke="#f59e0b"
              strokeWidth={3}
              fill="url(#netIncomeAreaFill)"
              fillOpacity={1}
              dot={(props) => {
                const { cx, cy, payload } = props;
                const value = payload?.netIncomeValue;
                if (value === null || value === undefined) return null;
                if (payload?.isIpoPeriod) {
                  return (
                    <g key={`ipo-net-${payload.period}`}>
                      <circle cx={cx} cy={cy} r={8} fill={ipoAccent} fillOpacity={0.25} />
                      <circle cx={cx} cy={cy} r={5.5} fill={ipoAccent} stroke="#111827" strokeWidth={1.5} />
                    </g>
                  );
                }
                if (payload?.isPreliminary) {
                  return (
                    <g key={`prelim-net-${payload.period}`}>
                      <circle cx={cx} cy={cy} r={9} fill={prelimAccent} fillOpacity={0.22} />
                      <circle cx={cx} cy={cy} r={5.5} fill={prelimAccent} stroke="#0f172a" strokeWidth={1.75} />
                    </g>
                  );
                }
                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={5}
                    fill="#f59e0b"
                    stroke="white"
                    strokeWidth={1.5}
                  />
                );
              }}
              isAnimationActive={false}
            />
            {hasIpoTick ? (
              <ReferenceLine
                x={chartData.find((point) => point.isIpoPeriod)?.period}
                stroke={ipoAccent}
                strokeDasharray="4 4"
                strokeWidth={1.5}
                ifOverflow="extendDomain"
                label={{
                  value: chartData.find((point) => point.isIpoPeriod)?.ipoLabel
                    || getIpoPeriodLabel(chartData.find((point) => point.isIpoPeriod)?.sourceForm),
                  position: 'insideTopLeft',
                  fill: ipoAccent,
                  fontSize: 11,
                  fontWeight: 800,
                }}
              />
            ) : null}
            {hasPrelimTick ? (
              <ReferenceLine
                x={chartData.find((point) => point.isPreliminary)?.period}
                stroke={prelimAccent}
                strokeDasharray="3 3"
                strokeWidth={1.75}
                ifOverflow="extendDomain"
                label={{
                  value: chartData.find((point) => point.isPreliminary)?.preliminaryLabel
                    || getPreliminary8kLabel(),
                  position: 'insideTopRight',
                  fill: prelimAccent,
                  fontSize: 11,
                  fontWeight: 800,
                }}
              />
            ) : null}
          </AreaChart>
            );
          })()}
        </ResponsiveContainer>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 1, sm: 1.5 },
            mt: 0.5,
            mb: 0.5,
          }}
        >
          {[
            { label: 'Revenue', color: '#8884d8' },
            { label: 'Operating Income', color: '#82ca9d' },
            { label: 'Net Income', color: '#f59e0b' },
          ].map((entry) => (
            <Box key={entry.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: entry.color }} />
              <Typography variant="caption" sx={{ color: entry.color, fontWeight: 700, lineHeight: 1.2 }}>
                {entry.label}
              </Typography>
            </Box>
          ))}
        </Box>
        </Box>
      </Paper>
      </>
      )}

      {isEtfDashboard && (
        <Paper
          elevation={2}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 3,
            order: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.25 }}>
                ETF Filing Snapshot
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Key balance-sheet values from the latest ETF filing period.
              </Typography>
            </Box>
            {etfBalanceSnapshot?.period ? (
              <Chip
                size="small"
                variant="outlined"
                label={`As of ${new Date(etfBalanceSnapshot.period).toLocaleDateString()}`}
              />
            ) : null}
          </Box>

          {Number.isFinite(etfTotalAssets) || Number.isFinite(etfTotalLiabilities) || Number.isFinite(etfNetAssets) ? (
            <ResponsiveContainer width="100%" height={isMobile ? 240 : 280}>
              <BarChart
                data={[
                  { label: 'Total Assets', value: Number.isFinite(etfTotalAssets) ? etfTotalAssets : 0, color: '#2563eb' },
                  { label: 'Liabilities', value: Number.isFinite(etfTotalLiabilities) ? etfTotalLiabilities : 0, color: '#ef4444' },
                  { label: 'Net Assets', value: Number.isFinite(etfNetAssets) ? etfNetAssets : 0, color: '#10b981' },
                  { label: 'Cash', value: Number.isFinite(etfCash) ? etfCash : 0, color: '#f59e0b' },
                ]}
                margin={{ top: 8, right: 20, left: 8, bottom: 8 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                <XAxis dataKey="label" tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 11 }} />
                <YAxis tick={{ fill: theme.palette.text.secondary, fontSize: isMobile ? 10 : 11 }} tickFormatter={(value) => formatUSD(Number(value))} width={96} />
                <Tooltip
                  formatter={(value) => formatUSD(Number(value))}
                  contentStyle={{
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(17,24,39,0.96)' : '#ffffff',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 8,
                  }}
                />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {[
                    { color: '#2563eb' },
                    { color: '#ef4444' },
                    { color: '#10b981' },
                    { color: '#f59e0b' },
                  ].map((entry, idx) => (
                    <Cell key={`etf-filing-bar-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              Latest ETF balance-sheet metrics are not available yet.
            </Typography>
          )}
        </Paper>
      )}

      {isEtfDashboard && (
        <Paper
          elevation={2}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 3,
            order: 3,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.25 }}>
                ETF Holdings Composition
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Top holdings and concentration profile for this ETF.
              </Typography>
            </Box>
            {etfHoldingsSource ? <Chip size="small" variant="outlined" label={`Source: ${etfHoldingsSource}`} /> : null}
          </Box>

          {etfHoldingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
              <CircularProgress size={26} />
            </Box>
          ) : etfHoldingsChartData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 320}>
                <PieChart>
                  <Pie
                    data={etfHoldingsChartData}
                    dataKey="weightPct"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={isMobile ? 84 : 108}
                    innerRadius={isMobile ? 44 : 58}
                    paddingAngle={1.5}
                    labelLine={false}
                    label={({ name, weightPct }) => `${name} ${Number(weightPct || 0).toFixed(1)}%`}
                  >
                    {etfHoldingsChartData.map((entry, index) => {
                      const palette = ['#2563eb', '#14b8a6', '#f59e0b', '#8b5cf6', '#ef4444', '#10b981', '#06b6d4', '#f97316', '#84cc16', '#a855f7'];
                      return <Cell key={`etf-holding-cell-${entry.name}-${index}`} fill={palette[index % palette.length]} />;
                    })}
                  </Pie>
                  <Tooltip formatter={(value, _name, meta) => [`${Number(value || 0).toFixed(2)}%`, meta?.payload?.fullName || meta?.payload?.name || 'Holding']} />
                </PieChart>
              </ResponsiveContainer>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {etfHoldingsChartData.slice(0, 8).map((entry) => (
                  <Chip
                    key={`etf-holding-chip-${entry.name}`}
                    size="small"
                    variant="outlined"
                    label={`${entry.name}: ${Number(entry.weightPct || 0).toFixed(2)}%`}
                  />
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
              ETF holdings data is currently unavailable for this ticker.
            </Typography>
          )}
        </Paper>
      )}

      {isEtfDashboard && (
        <Paper
          elevation={2}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: 3,
            order: 4,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
            <Box>
              <Typography variant="h6" sx={{ mb: 0.25 }}>
                ETF Regulatory Filings (SEC)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Fund-form filings such as N-PORT, N-CEN, N-CSR, and related amendments.
              </Typography>
            </Box>
            <Chip
              size="small"
              variant="outlined"
              label={`${etfFundFilings.length} recent fund filings`}
            />
          </Box>

          {etfFundFilingsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : etfFundFilings.length > 0 ? (
            <>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                {Array.from(etfFundFilings.reduce((acc, filing) => {
                  const form = String(filing?.form || '').toUpperCase() || 'UNKNOWN';
                  acc.set(form, (acc.get(form) || 0) + 1);
                  return acc;
                }, new Map()).entries())
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 8)
                  .map(([form, count]) => (
                    <Chip
                      key={`etf-fund-form-chip-${form}`}
                      size="small"
                      variant="outlined"
                      label={`${form}: ${count}`}
                    />
                  ))}
              </Box>

              <Grid container spacing={1}>
                {etfFundFilings.slice(0, 8).map((filing) => {
                  const form = String(filing?.form || '').toUpperCase() || 'UNKNOWN';
                  const filingDate = filing?.filingDate ? new Date(filing.filingDate) : null;
                  const reportDate = filing?.reportDate ? new Date(filing.reportDate) : null;
                  const hasUrl = Boolean(String(filing?.filingUrl || '').trim());
                  return (
                    <Grid item xs={12} md={6} key={`etf-fund-filing-${filing?.accessionNumber || `${form}-${filing?.filingDate || ''}`}`}>
                      <Paper variant="outlined" sx={{ p: 1.25, height: '100%' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{form}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {filingDate && !Number.isNaN(filingDate.getTime()) ? filingDate.toLocaleDateString() : 'Date N/A'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.6 }}>
                          {reportDate && !Number.isNaN(reportDate.getTime())
                            ? `Report date: ${reportDate.toLocaleDateString()}`
                            : 'Report date: N/A'}
                        </Typography>
                        {hasUrl ? (
                          <a href={filing.filingUrl} target="_blank" rel="noopener noreferrer">
                            View filing document
                          </a>
                        ) : (
                          <Typography variant="caption" color="text.secondary">Filing URL unavailable</Typography>
                        )}
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              No recent SEC fund-form filings are currently available for this ETF.
            </Typography>
          )}
        </Paper>
      )}

      {/* TradingView Price Chart */}
      {!isWatchlistIndex && (
        <Box sx={{ mb: 3, order: 6 }}>
          <TradingView
            ticker={ticker}
            exchange={data.companyOverview?.exchange || exchange}
            showFinancialBars={false}
          />
        </Box>
      )}

    </Box>
  );
};

export default CompanyDashboard;
